import React, { useEffect, useState, useRef, useCallback , useMemo} from "react";
import {
  getSidebarCollapseOverride,
  isFullScreenChartRoute,
  subscribeSidebarCollapse,
} from "../../utils/sidebarCollapse";
import { flushSync } from "react-dom";
import { format } from "d3-format";
import { curveMonotoneX } from "d3-shape";
import { v4 as uuidv4 } from "uuid";

// import { last } from 'react-financial-charts/lib/utils';
import {
  Annotate,
  BarSeries,
  CandlestickSeries,
  Chart,
  ChartCanvas,
  CrossHairCursor,
  discontinuousTimeScaleProviderBuilder,
  Label,
  LabelAnnotation,
  lastVisibleItemBasedZoomAnchor,
  LineSeries,
  MouseCoordinateX,
  MouseCoordinateY,
  MovingAverageTooltip,
  OHLCSeries,
  OHLCTooltip,
  SARSeries,
  XAxis,
  YAxis,
} from "react-financial-charts";
import { timeFormat } from "d3-time-format";
import PPOIndicator from "./indicators/PPOIndicator";
import PVOIndicator from "./indicators/PVOIndicator";
import MACDHIndicator from "./indicators/MACDHIndicator";
import MACDIndicator from "./indicators/MACDIndicator";
import CCIIndicator from "./indicators/CCIIndicator";
import CMFIndicator from "./indicators/CMFIndicator";
import { nFormatter } from "../../utils/utils";
import AnnotationStage from "./AnnotationStage";
import RSIIndicator from "./indicators/RSIIndicator";
import MFIIndicator from "./indicators/MFIIndicator";
import EFIndicator from "./indicators/EFIIndicator";
import ADXIndicator from "./indicators/ADXIndicator";
import OBVIndicator from "./indicators/OBVIndicator";
import ROCIndicator from "./indicators/ROCIndicator";
import STOCHIndicator from "./indicators/STOCHIndicator";
import moment from "moment";
import { useLocation } from "react-router-dom"; // Assuming you're using React Router for routing
import TooltipPriceLabel from "./TooltipPriceLabel";
import CalloutAnnotation from "./CalloutAnnotation";
import { squareExportPanelHeight } from "./squareExport";

// Label for an indicator that owns its own panel (ADL, RSI, MACD, …).
//
// Client, Indicator Polish 1: "Keep the parameter only when meaningful, such as
// `RSI (14)`. Display only `ADL` if its `0` parameter has no purpose."
//
// "Meaningful" is decided by the value, not by a per-indicator list: the
// indicator picker gives every indicator a default parameter string, and the
// ones that take no parameter are given the placeholder "0" (see the
// defaultParams table in the Indicators modal). So a parameter set made
// entirely of zeros is the placeholder and is dropped; anything else — "14",
// "12,26,9", "20,2" — is a real setting and is shown.
//
// Kept a plain function rather than a hook so it can be called inside the
// indicator render loop without ordering constraints.
// The two fixed bars pinned to the bottom of the mobile chart screen, and how
// much of the flat 170px reservation in the height maths each one accounts for.
// `reserved` is the figure baked into that constant, NOT the bar's measured
// height, so this stays in lockstep with the matching #chart-component rule in
// index.css — the container and the canvas have to agree or one clips the other.
const BOTTOM_CHROME_BARS = [
  { selector: ".mobile-chart-toolbar", reserved: 50 },
  { selector: ".bottom-navbar-container", reserved: 64 },
];

export const indicatorPanelLabel = (indicator) => {
  const id = indicator?.id || "";
  const raw = indicator?.params;
  if (raw === undefined || raw === null) return id;

  const parts = String(raw)
    .split(",")
    .map((p) => p.trim())
    .filter((p) => p !== "");

  if (parts.length === 0) return id;
  // Every part is a zero → the picker's placeholder, not a setting the user chose.
  if (parts.every((p) => Number(p) === 0)) return id;

  return `${id} (${parts.join(", ")})`;
};

// How far the crosshair's price badge sits inside the price-axis gutter.
//
// Client, 0014839: "crosshair price bar covers active bar on league play trade
// screen." The badge is anchored to the right edge of the chart and was sized
// to the WHOLE gutter (margin.right - 2), so its left edge landed on the plot
// boundary — the exact pixel column where the newest candle is drawn. Measured
// on their own two screenshots at 1920px: the badge starts at x=1408 in both,
// and the last candle ends at x=1407 in League Play and x=1399 on the main
// screen. Same component, same badge; League Play's sidebar is a few pixels
// wider, which is the whole reason the bar lands under the badge there first.
//
// The gutter's first 8px carry nothing: the right YAxis draws a 4px tick mark
// and pads another 4px before the number (innerTickSize + tickPadding, both
// react-financial-charts defaults - see YAxis.defaultProps). So the badge can
// start 8px in and still cover every digit the axis prints. The 9th pixel is
// the badge's own `right: 1` offset.
//
// NOT fixed by widening the gutter: margin.right's buffer is 20 desktop / 10
// mobile because the client asked for it ("the Y-axis looks like it's not using
// the full width anymore"), and growing it would move the badge right back out
// with it. This keeps the axis exactly as it is and only stops the badge
// spilling out of it.
const BADGE_AXIS_INSET = 9;

// The main chart's top margin, in pixels.
//
// Client, follow-up to 0014839: "either the crosshairs are off, or the y-axis of
// the chart is off ... try lining up the y-axis value with the crosshairs. It
// doesn't match perfectly; it's a little off."
//
// Measured on their two BTCUSD 1h screenshots. Resting the crosshair on a
// candle's high wick printed 76,840.15 on the badge against an actual high of
// 76,869.00; on the same candle's low wick it printed 76,158.84 against an
// actual low of 76,186.47. Both readings sat ~28 price units LOW, one near the
// top of the panel and one near the bottom. At the ~4.85 units-per-pixel that
// chart was showing (500 units per 103px), 28 units is a flat SIX pixels in
// both places — a constant offset, so the scale was never the problem.
//
// Six pixels is exactly margin.top, and that is the whole bug. The crosshair's
// horizontal LINE is a CSS overlay positioned in chartContainerRef's coordinate
// space, where y=0 is the container's top. yScale belongs to the ChartCanvas
// <g> that react-financial-charts translates down by margin.top, so yScale's
// y=0 is the top of the PLOT AREA, six pixels lower. Handing the
// container-relative y straight to yScale.invert() therefore reports the price
// six pixels BELOW the line the badge is meant to be labelling.
//
// The candles, the axis ticks and the last-price line are all drawn inside that
// same translated <g>, so every one of them was already right. Only the badge
// disagreed — which is exactly why the client could see a mismatch but could
// not tell whether to blame the crosshair or the axis, and why the candle and
// live-price data looked wrong to them when it was not.
//
// Lives at module scope and feeds the `margin` memo below so the two cannot
// drift: the container -> plot-area conversion is only correct for as long as
// this is the same number the canvas is translated by.
const CHART_MARGIN_TOP = 6;

const ChartComp = ({
  initialData = [],
  setting,
  setSetting,
  gridSetting,
  setIndicatorConfig,
  indicatorConfig = [],
  symbol,
  stockData,
  onLoadMore,
  isLoadingMore = false,
  setIsLoadingMore,
  hideZoomControls = false,
  squareExport = null
}) => {
  const [lines, setLines] = useState([]);

  // Track whether trendline endpoints overlap the zoom control buttons
  const [endpointsOverControls, setEndpointsOverControls] = useState(false);
  const handleEndpointsOverControlsChange = useCallback((value) => {
    setEndpointsOverControls(value);
  }, []);

  const location = useLocation();

  const ScaleProvider =
    discontinuousTimeScaleProviderBuilder().inputDateAccessor(
      (d) => new Date(d.date)
    );

  // Process initialData through ScaleProvider to get scaled data and accessors
  const { data, xScale, xAccessor, displayXAccessor } = ScaleProvider(initialData);

  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  // The window size the chart was last sized against.
  //
  // The height/width memo below READS window.innerHeight and window.innerWidth
  // but had no way to know they had changed: its only resize-driven dependency
  // was isMobileView, which tracks a single 768px WIDTH threshold. So a change in
  // viewport HEIGHT never recomputed anything, and the chart kept whatever size
  // it happened to be given the last time some other dependency changed.
  //
  // Client: "when you pull up an asset from the search feature on the trade
  // screen check out the chart." Picking a result navigates to /chartSearch/<sym>
  // — and on a phone the keyboard is still up at that instant, so the layout
  // viewport is short. location.pathname IS a dependency, so the memo recomputed
  // right then, against the shrunken viewport, and clamped the chart to its 200px
  // minimum. The keyboard then closed and the viewport went back to full height —
  // but no dependency had changed, so the memo never ran again. The chart stayed
  // at 200px with the rest of the screen blank underneath.
  //
  // Reproduced at 390x844: chart 674px → keyboard up (370px) + navigate → 200px →
  // keyboard down → still 200px, with 469px of empty space below it.
  //
  // Holding the numbers in state fixes it for every cause, not just this one:
  // rotation, a resized desktop window, and the keyboard all now re-run the memo.
  //
  // window.resize ONLY — deliberately not visualViewport. On iOS the keyboard
  // overlays the page without changing the layout viewport, so window.innerHeight
  // does not move and there is nothing to correct; wiring visualViewport in would
  // instead make the chart shrink behind the keyboard, which is the bug rather
  // than the fix.
  const [viewportSize, setViewportSize] = useState(() => ({
    height: window.innerHeight,
    width: window.innerWidth,
  }));

  // How much of the flat 170px bottom reservation below is currently NOT on
  // screen, and can therefore be given back to the chart.
  //
  // The mobile branch of the height maths subtracts a constant 170px — header
  // 56 + bottom nav 64 + chart toolbar 50 — for chrome it assumes is always
  // painted. Two of those three are not always painted. While the symbol search
  // is open and the keyboard is up, DashboardHeader sets body.chart-search-open
  // and index.css display:none's BOTH .mobile-chart-toolbar and
  // .bottom-navbar-container (AuthLayout's body.keyboard-open hides the nav on
  // every input screen for the same reason). The keyboard ALSO shrinks the
  // layout viewport, because index.html asks for
  // interactive-widget=resizes-content — so at that moment the chart is measured
  // against a much shorter viewport and is still told to leave room for 114px of
  // bars that are not there.
  //
  // Measured at 393x852 emulating an iPhone, keyboard up (viewport 470px): the
  // chart ended at y=399 with 71px of blank white between its x-axis and the top
  // of the keyboard, exactly the band in the client's screenshot.
  //
  // Additive by construction: a bar that is absent from the DOM is skipped (that
  // is a screen which never had it, and the constant is not ours to change
  // there), and a bar that is visible contributes nothing. With both bars
  // painted this is 0 and the arithmetic is byte-identical to the old constant.
  const [reclaimedBottomChrome, setReclaimedBottomChrome] = useState(0);
  const [sidebarCollapsedTick, setSidebarCollapsedTick] = useState(0);
  useEffect(() => subscribeSidebarCollapse(() => setSidebarCollapsedTick((t) => t + 1)), []);
  const intervalKey = `${symbol || "NA"}-${setting?.period || "default"}`;

  // Mobile crosshair state - starts OFF. Only a confirmed tap on the chart
  // (handled in onChartTouchEnd) activates it. This prevents the crosshair
  // from appearing during page load or while the user is scrolling.
  const [showMobileCrosshair, setShowMobileCrosshair] = useState(false);
  // Ref mirror of showMobileCrosshair so window-level touch listeners (whose
  // closures may otherwise go stale) can read the current value at event time.
  const showMobileCrosshairRef = useRef(false);
  useEffect(() => {
    showMobileCrosshairRef.current = showMobileCrosshair;
  }, [showMobileCrosshair]);

  // State to track current hover/crosshair data
  const [currentHoverData, setCurrentHoverData] = useState(null);

  // State to track hover price for custom CSS price badge (independent of ChartCanvas events)
  const [hoverPrice, setHoverPrice] = useState(null); // { price, yPx, mainChartTop }
  const [isOverYAxis, setIsOverYAxis] = useState(false);

  // Hover info for the bottom-of-stack date/time overlay (TradingView-style).
  // Set on mouse-move anywhere over the chart wrapper; cleared on mouse-leave.
  const [chartHoverInfo, setChartHoverInfo] = useState(null); // { x, dateStr } | null

  // State to track OHLC data for display at top of chart
  const [currentOHLC, setCurrentOHLC] = useState({ open: 0, high: 0, low: 0, close: 0 });

  // State to sync xExtents between main chart and indicator charts
  const [syncedXExtents, setSyncedXExtents] = useState(null);

  // Ref to track chart scales for real-time annotation alignment during scroll/zoom
  // Using a ref + forceUpdate counter for smooth updates without state batching delays
  const chartScalesRef = useRef({ xScale: null, yScale: null });
  const [scaleUpdateCounter, setScaleUpdateCounter] = useState(0);

  // Track pan offset for real-time trendline movement during scroll
  // This stores the pixel delta to apply as CSS transform during pan gestures
  const annotationPanOffsetRef = useRef({ x: 0, y: 0 });
  const panStartRef = useRef(null); // Track pan start position { x, y, xDomain }
  const isPanningRef = useRef(false);
  const annotationStageRef = useRef(null); // Ref to the AnnotationStage wrapper div
  const belowIndicatorContainerRef = useRef(null); // Ref for below indicator charts for real-time pan sync
  const indicatorCrosshairRef = useRef(null); // Ref for crosshair vertical line on indicator panels
  const horizontalCrosshairRef = useRef(null); // Ref for crosshair horizontal line on main chart
  // Mobile tap-vs-scroll discrimination for toggling the crosshair.
  const tapTrackingRef = useRef({ startX: 0, startY: 0, startTime: 0, isTap: false });
  // Indirection so the touch-end handler can call handleToggleCrosshair without
  // creating a temporal-dead-zone reference (the toggle is defined further down).
  const handleToggleCrosshairRef = useRef(null);

  // Refs for indicator chart instances to enable bidirectional sync
  const indicatorChartRefsRef = useRef({});

  // Track initialization for xExtents
  const hasInitializedRef = useRef(false);
  const prevDataLengthRef = useRef(0);
  const prevFirstBarDateRef = useRef(null); // Track first bar's date to detect prepend vs append
  const preservedDomainRef = useRef(null); // Store current viewport when loading more data
  const preZoomDomainRef = useRef(null); // Store viewport position before first zoom operation

  // Touch handling for mobile - both pan and pinch zoom
  const pinchStateRef = useRef(null);
  const pendingPinchFrameRef = useRef(null); // Track pending pinch animation frame
  const chartContainerRef = useRef(null);
  const chartGroupRef = useRef(null);
  const chartCanvasComponentRef = useRef(null);
  const prevIntervalKeyRef = useRef(intervalKey);
  const lastViewportSpanRef = useRef(isMobileView ? 50 : 100);
  const lastXExtentsRef = useRef(undefined);

  // Track which chart the user is currently interacting with
  const activeChartRef = useRef('main'); // 'main' or indicator id
  const lastMainDomainRef = useRef(null);
  const lastIndicatorDomainsRef = useRef({});
  const indicatorDataLengthsRef = useRef({}); // Track each indicator's data length for proportional sync

  // Specialized indicators with their own data/index space (NOT shared with main chart)
  const SPECIALIZED_INDICATOR_IDS = useMemo(() => new Set(
    []
  ), []);

  // Shared helper: sync all indicator charts to a given main-chart domain.
  // Updates lastMainDomainRef (prevents polling double-sync), applies proportional
  // mapping for specialized indicators, and uses flushSync for synchronous render.
  const syncIndicatorsToMainDomain = useCallback((newDomain) => {
    if (!newDomain || newDomain.length !== 2) return;

    const mainLen = data?.length || 0;

    // 1. Update refs to prevent polling loop from double-syncing
    lastMainDomainRef.current = [...newDomain];
    lastXExtentsRef.current = [...newDomain];

    // 2. Sync all indicator charts with proportional mapping for specialized ones
    Object.entries(indicatorChartRefsRef.current).forEach(([indicatorId, indicatorRef]) => {
      if (!indicatorRef?.xAxisZoom) return;

      if (SPECIALIZED_INDICATOR_IDS.has(indicatorId)) {
        const indicatorLen = indicatorDataLengthsRef.current[indicatorId] || mainLen;
        if (indicatorLen > 0 && mainLen > 0) {
          const ratio = indicatorLen / mainLen;
          const mappedStart = Math.round(newDomain[0] * ratio);
          const mappedEnd = Math.round(newDomain[1] * ratio);
          const clampedStart = Math.max(0, Math.min(mappedStart, indicatorLen - 1));
          const clampedEnd = Math.max(clampedStart, Math.min(mappedEnd, indicatorLen - 1));
          indicatorRef.xAxisZoom([clampedStart, clampedEnd]);
        }
      } else {
        indicatorRef.xAxisZoom(newDomain);
      }
      lastIndicatorDomainsRef.current[indicatorId] = [...newDomain];
    });

    // 3. Flush synchronous React update so indicators render in the same frame
    flushSync(() => {
      setSyncedXExtents([newDomain[0], newDomain[1]]);
    });
  }, [data?.length, SPECIALIZED_INDICATOR_IDS]);

  // Sync all charts to a given domain (prevents loops with activeChartRef)
  const syncAllCharts = useCallback((domain, sourceChartId) => {
    if (!domain || domain.length !== 2) return;

    // Sync main chart if source is not main
    if (sourceChartId !== 'main' && chartCanvasComponentRef.current?.xAxisZoom) {
      chartCanvasComponentRef.current.xAxisZoom(domain);
    }

    // Sync all indicator charts except the source
    Object.entries(indicatorChartRefsRef.current).forEach(([indicatorId, indicatorRef]) => {
      if (indicatorId !== sourceChartId && indicatorRef?.xAxisZoom) {
        indicatorRef.xAxisZoom(domain);
      }
    });

    // Update lastXExtentsRef to prevent xExtents recalculation from reverting scroll
    lastXExtentsRef.current = [...domain];
  }, []);

  // Bidirectional sync: Poll both main chart and indicator charts
  // When any chart's domain changes, sync all others
  useEffect(() => {
    let animationFrameId;

    let isSyncing = false;

    const checkAndSync = () => {
      if (isSyncing) {
        animationFrameId = requestAnimationFrame(checkAndSync);
        return;
      }

      // Check main chart domain (main → indicators sync)
      if (chartCanvasComponentRef.current?.state?.xScale) {
        const mainDomain = chartCanvasComponentRef.current.state.xScale.domain();
        if (mainDomain && mainDomain.length === 2) {
          const mainChanged = !lastMainDomainRef.current ||
            mainDomain[0] !== lastMainDomainRef.current[0] ||
            mainDomain[1] !== lastMainDomainRef.current[1];

          if (mainChanged) {
            isSyncing = true;
            // Use shared helper for consistent sync (proportional mapping + flushSync)
            syncIndicatorsToMainDomain(mainDomain);
            isSyncing = false;
          }
        }
      }

      // Check indicator domains for reverse sync (indicator zoom → main chart)
      // When user zooms on an indicator, its internal domain changes but main chart doesn't know
      const mainLen = data?.length || 0;
      if (mainLen > 0) {
        for (const [indicatorId, indicatorRef] of Object.entries(indicatorChartRefsRef.current)) {
          if (!indicatorRef?.state?.xScale) continue;

          const indicatorDomain = indicatorRef.state.xScale.domain();
          if (!indicatorDomain || indicatorDomain.length !== 2) continue;

          const lastDomain = lastIndicatorDomainsRef.current[indicatorId];

          // If we haven't tracked this indicator's domain yet, initialize it
          // so we can detect changes on the next frame
          if (!lastDomain) {
            lastIndicatorDomainsRef.current[indicatorId] = [...indicatorDomain];
            continue;
          }

          const indicatorChanged =
            indicatorDomain[0] !== lastDomain[0] || indicatorDomain[1] !== lastDomain[1];

          if (indicatorChanged) {
            isSyncing = true;

            // Reverse-map indicator domain to main chart domain
            let newMainDomain;
            if (SPECIALIZED_INDICATOR_IDS.has(indicatorId)) {
              const indicatorLen = indicatorDataLengthsRef.current[indicatorId] || mainLen;
              if (indicatorLen > 0) {
                const ratio = indicatorLen / mainLen;
                newMainDomain = [
                  Math.round(indicatorDomain[0] / ratio),
                  Math.round(indicatorDomain[1] / ratio)
                ];
              }
            } else {
              newMainDomain = [...indicatorDomain];
            }

            if (newMainDomain) {
              // Sync main chart
              if (chartCanvasComponentRef.current?.xAxisZoom) {
                chartCanvasComponentRef.current.xAxisZoom(newMainDomain);
              }
              // Sync all indicators via shared helper
              syncIndicatorsToMainDomain(newMainDomain);
            }

            isSyncing = false;
            break; // One indicator changed is enough to trigger full sync
          }
        }
      }

      animationFrameId = requestAnimationFrame(checkAndSync);
    };

    // Initialize lastMainDomainRef with current domain to prevent false change detection
    // Without this, the first checkAndSync sees null → triggers sync → indicators flicker
    const initTimeout = setTimeout(() => {
      if (chartCanvasComponentRef.current?.state?.xScale) {
        const initialDomain = chartCanvasComponentRef.current.state.xScale.domain();
        if (initialDomain && initialDomain.length === 2) {
          lastMainDomainRef.current = [...initialDomain];
          lastXExtentsRef.current = [...initialDomain];
        }
      }
      animationFrameId = requestAnimationFrame(checkAndSync);
    }, 100);

    return () => {
      clearTimeout(initTimeout);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [intervalKey, syncIndicatorsToMainDomain, data?.length, SPECIALIZED_INDICATOR_IDS]); // Re-initialize when interval changes

  // Proactive wheel-scroll sync: When user scrolls on main chart, sync indicators
  // immediately instead of waiting for the RAF polling loop to detect the change.
  useEffect(() => {
    const container = chartContainerRef.current;
    if (!container) return;

    const handleMainChartWheel = () => {
      // Defer via rAF to let ChartCanvas process the wheel event and update its internal xScale first
      requestAnimationFrame(() => {
        if (chartCanvasComponentRef.current?.state?.xScale) {
          const newDomain = chartCanvasComponentRef.current.state.xScale.domain();
          if (newDomain && newDomain.length === 2) {
            syncIndicatorsToMainDomain(newDomain);
          }
        }
      });
    };

    container.addEventListener('wheel', handleMainChartWheel, { passive: true });

    return () => {
      container.removeEventListener('wheel', handleMainChartWheel);
    };
  }, [syncIndicatorsToMainDomain]);

  // Sync chart scales for trendline alignment during scroll/zoom
  // Updates on every animation frame for smooth real-time trendline movement
  // Track previous scales to detect actual changes and avoid unnecessary re-renders
  const prevScalesRef = useRef({ xDomain: null, yDomain: null, xRange: null, yRange: null });

  useEffect(() => {
    let animationFrameId;

    const syncScales = () => {
      if (chartCanvasComponentRef.current?.state) {
        const state = chartCanvasComponentRef.current.state;
        const xScale = state.xScale;
        // Get yScale from the main chart (id=3) - it's stored in chartConfigs
        const chartConfigs = state.chartConfigs;
        let yScale = null;
        if (chartConfigs) {
          // Find chart with id 3 (main candlestick chart)
          const mainChart = chartConfigs.find(c => c.id === 3);
          if (mainChart) {
            yScale = mainChart.yScale;
          }
        }

        // Always update the ref with latest scales for real-time access
        chartScalesRef.current = { xScale, yScale };

        // Check if scales actually changed to avoid unnecessary re-renders.
        //
        // RANGE is compared as well as DOMAIN, and that is the fix for the
        // client's "trendlines get out of place when you add indicators".
        //
        // A scale has two halves: the domain (which prices / bar indices it
        // covers) and the range (which pixels those map onto). Adding an
        // indicator panel shortens the PRICE panel — its yScale range goes from
        // e.g. [500, 0] to [180, 0] — while the prices on screen do not change,
        // so the DOMAIN is untouched.
        //
        // This guard used to look at the domain only. It therefore saw "nothing
        // changed", never bumped scaleUpdateCounter, and AnnotationStage's
        // recompute effect (which is keyed on that counter) never ran. Every
        // trendline kept the pixel coordinates it had been given for the TALL
        // panel and was left stranded in the short one — the exact drift in the
        // client's video. The same applied in reverse when an indicator was
        // removed and the price panel grew back.
        //
        // Comparing the range costs one extra array compare per animation frame
        // and makes the counter fire on any re-layout, which is precisely when
        // pixel positions derived from these scales have gone stale.
        const currentXDomain = xScale?.domain();
        const currentYDomain = yScale?.domain();
        const currentXRange = xScale?.range();
        const currentYRange = yScale?.range();
        const prevXDomain = prevScalesRef.current.xDomain;
        const prevYDomain = prevScalesRef.current.yDomain;
        const prevXRange = prevScalesRef.current.xRange;
        const prevYRange = prevScalesRef.current.yRange;

        const xChanged = !prevXDomain || !currentXDomain ||
          prevXDomain[0] !== currentXDomain[0] || prevXDomain[1] !== currentXDomain[1];
        const yChanged = !prevYDomain || !currentYDomain ||
          prevYDomain[0] !== currentYDomain[0] || prevYDomain[1] !== currentYDomain[1];
        const xRangeChanged = !prevXRange || !currentXRange ||
          prevXRange[0] !== currentXRange[0] || prevXRange[1] !== currentXRange[1];
        const yRangeChanged = !prevYRange || !currentYRange ||
          prevYRange[0] !== currentYRange[0] || prevYRange[1] !== currentYRange[1];

        if (xChanged || yChanged || xRangeChanged || yRangeChanged) {
          prevScalesRef.current = {
            xDomain: currentXDomain ? [...currentXDomain] : null,
            yDomain: currentYDomain ? [...currentYDomain] : null,
            xRange: currentXRange ? [...currentXRange] : null,
            yRange: currentYRange ? [...currentYRange] : null
          };
          // Skip trendline recalculation during active pan — the CSS transform
          // on annotationStageRef handles instant visual feedback instead.
          // Only recalculate when NOT panning (wheel zoom, programmatic changes, etc.)
          if (!isPanningRef.current && !isIndicatorPanningRef.current) {
            flushSync(() => {
              setScaleUpdateCounter(c => c + 1);
            });
          }
        }
      }
      animationFrameId = requestAnimationFrame(syncScales);
    };

    // Start syncing immediately, but also try once after a short delay
    // in case the chart needs time to initialize
    animationFrameId = requestAnimationFrame(syncScales);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [intervalKey]);

  const getDefaultViewportSpan = useCallback(
    () => (isMobileView ? 50 : 100),
    [isMobileView]
  );

  const updateViewportSpanFromDomain = useCallback((domain) => {
    if (!domain || domain.length < 2) return;
    const numericDomain = domain.map((value) =>
      value instanceof Date ? value.getTime() : value
    );
    const span = numericDomain[1] - numericDomain[0];
    if (Number.isFinite(span) && span > 0) {
      lastViewportSpanRef.current = span;
    }
  }, []);

  const [chartMainItemInitialPosition, setChartMainItemInitialPosition] =
    useState({
      top: "",
      left: "",
    });

  const aboveChartCounterRef = useRef(0); // Using a ref to track aboveChartCounter

  const [canvasStyle, setCanvasStyle] = useState({
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 7,
    pointerEvents: setting?.drawMode ? "auto" : "none",
  });

  // Track if we just changed interval (period/symbol) to force scroll to most recent data
  const justChangedIntervalRef = useRef(false);

  if (prevIntervalKeyRef.current !== intervalKey) {
    hasInitializedRef.current = false;
    prevDataLengthRef.current = 0;
    prevFirstBarDateRef.current = null; // Reset first bar date tracking
    preservedDomainRef.current = null; // Clear preserved domain on symbol change
    lastXExtentsRef.current = undefined; // Reset xExtents to prevent stale values
    lastViewportSpanRef.current = isMobileView ? 50 : 100; // Reset viewport span
    pinchStateRef.current = null;
    justChangedIntervalRef.current = true; // Mark that we just changed interval
    // Reset chart scales ref to prevent stale scales being used for trendlines
    chartScalesRef.current = { xScale: null, yScale: null };
    if (pendingPinchFrameRef.current) {
      cancelAnimationFrame(pendingPinchFrameRef.current);
      pendingPinchFrameRef.current = null;
    }
    prevIntervalKeyRef.current = intervalKey;
  }

  // Listen for resize to update isMobileView (for DevTools mobile emulation) and
  // the viewport size the chart is measured against — see viewportSize above.
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
      // Same object unless the numbers actually moved, so a resize that changes
      // nothing (or fires repeatedly at the same size) does not re-render.
      setViewportSize((prev) =>
        prev.height === window.innerHeight && prev.width === window.innerWidth
          ? prev
          : { height: window.innerHeight, width: window.innerWidth }
      );
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  // Watch the two fixed bottom bars so the height maths knows when they are
  // hidden — see reclaimedBottomChrome above. They are toggled by a class on
  // <body>, so a class MutationObserver catches the change the instant it
  // happens; the resize listener covers the bar being re-measured at a new
  // width.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const measureBottomChrome = () => {
      let reclaimed = 0;
      // Gated on the SAME class as the matching #chart-component rule in
      // index.css. That container carries height + overflow:hidden !important,
      // so it, not this, decides what the user sees: if the two ever disagreed,
      // the smaller one would clip the larger and the chart would either be cut
      // off or leave the white band back. One condition, both places.
      if (document.body.classList.contains("chart-search-open")) {
        BOTTOM_CHROME_BARS.forEach((bar) => {
          const el = document.querySelector(bar.selector);
          if (!el) return; // never rendered on this screen — not our reservation to touch
          if (window.getComputedStyle(el).display === "none") reclaimed += bar.reserved;
        });
      }
      setReclaimedBottomChrome((prev) =>
        Math.abs(prev - reclaimed) < 1 ? prev : reclaimed
      );
    };

    measureBottomChrome();
    const observer = new MutationObserver(measureBottomChrome);
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    window.addEventListener("resize", measureBottomChrome);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measureBottomChrome);
    };
  }, []);

  useEffect(() => {
    getChartMainItemPosition();

    const position = getChartMainItemPosition();

    setChartMainItemInitialPosition({
      top: position.top,
      left: position.left,
    });
  }, []);

  useEffect(() => {
    if (!Number.isFinite(lastViewportSpanRef.current) || lastViewportSpanRef.current <= 0) {
      lastViewportSpanRef.current = getDefaultViewportSpan();
    }
  }, [getDefaultViewportSpan]);

  useEffect(() => {
    setCanvasStyle((prevStyle) => ({
      ...prevStyle,
      pointerEvents: setting?.drawMode ? "auto" : "none",
      zIndex: setting?.drawMode ? 25 : 7,
    }));
  }, [setting?.drawMode]);

  // Track previous data length to detect historical data loading
  const prevDataLengthForClearRef = useRef(data.length);

  // Clear syncedXExtents when data length changes (historical data loaded)
  // This ensures xExtents (with shifted domain) takes priority over stale syncedXExtents
  useEffect(() => {
    const prevLen = prevDataLengthForClearRef.current;
    const currentLen = data.length;

    // Only clear if data length actually changed (not just on mount)
    if (prevLen !== currentLen && prevLen > 0) {
      console.log('[Chart] Data length changed, clearing syncedXExtents:', prevLen, '->', currentLen);
      setSyncedXExtents(null);
    }

    prevDataLengthForClearRef.current = currentLen;
  }, [data.length]);

  useEffect(() => {
    pinchStateRef.current = null;
    if (pendingPinchFrameRef.current) {
      cancelAnimationFrame(pendingPinchFrameRef.current);
      pendingPinchFrameRef.current = null;
    }
    if (chartCanvasComponentRef.current?.resetYDomain) {
      try {
        chartCanvasComponentRef.current.resetYDomain();
      } catch (error) {
        console.debug("[Chart] resetYDomain skipped:", error);
      }
    }
  }, [intervalKey]);

  // Prevent text selection and context menu on chart
  useEffect(() => {
    const chartContainer = chartContainerRef.current;
    if (!chartContainer) return;

    const preventSelection = (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    const clearSelection = () => {
      if (window.getSelection) {
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
        }
      }
      if (document.selection) {
        document.selection.empty();
      }
    };

    // Prevent various selection events
    chartContainer.addEventListener('selectstart', preventSelection, { passive: false });
    chartContainer.addEventListener('contextmenu', preventSelection, { passive: false });
    chartContainer.addEventListener('copy', preventSelection, { passive: false });
    chartContainer.addEventListener('cut', preventSelection, { passive: false });
    chartContainer.addEventListener('paste', preventSelection, { passive: false });

    // Clear any accidental selections
    chartContainer.addEventListener('mousedown', clearSelection);
    chartContainer.addEventListener('touchstart', clearSelection);

    return () => {
      chartContainer.removeEventListener('selectstart', preventSelection);
      chartContainer.removeEventListener('contextmenu', preventSelection);
      chartContainer.removeEventListener('copy', preventSelection);
      chartContainer.removeEventListener('cut', preventSelection);
      chartContainer.removeEventListener('paste', preventSelection);
      chartContainer.removeEventListener('mousedown', clearSelection);
      chartContainer.removeEventListener('touchstart', clearSelection);
    };
  }, []);

  // Real-time pan tracking for trendline synchronization
  // During active pan: CSS transform provides instant visual feedback (no React render)
  // The syncScales RAF loop skips scaleUpdateCounter updates while panning to avoid conflict
  // On pan end: CSS transform cleared, scaleUpdateCounter incremented for final recalculation

  // Apply CSS transform to annotation layer during pan (instant, GPU-accelerated)
  const applyPanTransform = useCallback((deltaX) => {
    if (annotationStageRef.current) {
      annotationStageRef.current.style.transform = `translateX(${deltaX}px)`;
      annotationStageRef.current.style.willChange = 'transform';
    }
  }, []);

  // Reset transform and force final trendline recalculation at pan end
  const resetPanTransform = useCallback(() => {
    if (annotationStageRef.current) {
      annotationStageRef.current.style.transform = '';
      annotationStageRef.current.style.willChange = '';
    }
    annotationPanOffsetRef.current = { x: 0, y: 0 };

    // Force scale sync
    flushSync(() => {
      setScaleUpdateCounter(c => c + 1);
    });
  }, []);

  const handleChartPanStart = useCallback((clientX, clientY) => {
    // Don't track pan if in draw mode
    if (setting?.drawMode) return;

    console.log('[Pan] handlePanStart called');
    isPanningRef.current = true;
    panStartRef.current = {
      x: clientX,
      y: clientY,
      xDomain: chartCanvasComponentRef.current?.state?.xScale?.domain()?.slice() || null
    };
    annotationPanOffsetRef.current = { x: 0, y: 0 };
  }, [setting?.drawMode]);

  const handleChartPanMove = useCallback((clientX, clientY) => {
    if (!isPanningRef.current || !panStartRef.current) return;

    const deltaX = clientX - panStartRef.current.x;
    annotationPanOffsetRef.current.x = deltaX;

    console.log('[Pan] handlePanMove deltaX:', deltaX);

    // Apply CSS transform for instant trendline movement during pan
    // Mobile: Invert direction (-deltaX) to match bar movement direction
    // Desktop: Use deltaX directly
    const transformDelta = isMobileView ? -deltaX : deltaX;
    applyPanTransform(transformDelta);

    // Real-time indicator sync: Calculate domain shift from pan delta
    // and apply to all indicator charts
    if (panStartRef.current.xDomain && chartCanvasComponentRef.current?.state?.xScale) {
      const xScale = chartCanvasComponentRef.current.state.xScale;
      const [startDomain, endDomain] = panStartRef.current.xDomain;

      // Convert pixel delta to domain delta
      // xScale maps domain values to pixel positions
      // We need to reverse: pixel delta → domain delta
      const domainRange = endDomain - startDomain;
      const pixelRange = xScale.range()[1] - xScale.range()[0];

      // Domain shift per pixel
      // Mobile: Use positive deltaX (inverted from desktop) because mobile touch direction is opposite
      // Desktop: Use negative deltaX (dragging right shows earlier data)
      const domainPerPixel = domainRange / pixelRange;
      const effectiveDelta = isMobileView ? deltaX : -deltaX;
      const domainShift = effectiveDelta * domainPerPixel;

      // Calculate new domain
      const newDomain = [startDomain + domainShift, endDomain + domainShift];

      // Sync all indicators using shared helper (proportional mapping + flushSync + lastMainDomainRef update)
      syncIndicatorsToMainDomain(newDomain);
    }
  }, [isMobileView, applyPanTransform, syncIndicatorsToMainDomain]);

  const handleChartPanEnd = useCallback(() => {
    if (!isPanningRef.current) return;

    console.log('[Pan] handlePanEnd called');
    isPanningRef.current = false;
    panStartRef.current = null;

    // Reset transform and recalculate actual positions
    resetPanTransform();
  }, [resetPanTransform]);

  // Mouse event handlers for chart pan
  const onChartMouseDown = useCallback((e) => {
    // Only track left mouse button, and not when clicking on controls
    if (e.button !== 0) return;
    if (e.target.closest('.chart-zoom-btn')) return;
    handleChartPanStart(e.clientX, e.clientY);
  }, [handleChartPanStart]);

  const onChartMouseMove = useCallback((e) => {
    if (!isPanningRef.current) return;
    handleChartPanMove(e.clientX, e.clientY);
  }, [handleChartPanMove]);

  const onChartMouseUp = useCallback(() => {
    handleChartPanEnd();
  }, [handleChartPanEnd]);

  const onChartMouseLeave = useCallback(() => {
    handleChartPanEnd();
  }, [handleChartPanEnd]);

  // Touch event handlers for chart pan. Tap detection (for toggling the mobile
  // crosshair) lives in a separate window-level capture-phase effect below so
  // it isn't suppressed by the chart canvas's internal touch handling.
  const onChartTouchStart = useCallback((e) => {
    if (e.touches.length !== 1) return;
    if (e.target.closest('.chart-zoom-btn')) return;
    const touch = e.touches[0];
    handleChartPanStart(touch.clientX, touch.clientY);
  }, [handleChartPanStart]);

  const onChartTouchMove = useCallback((e) => {
    if (!isPanningRef.current || e.touches.length !== 1) return;
    const touch = e.touches[0];
    handleChartPanMove(touch.clientX, touch.clientY);
  }, [handleChartPanMove]);

  const onChartTouchEnd = useCallback(() => {
    handleChartPanEnd();
  }, [handleChartPanEnd]);

  // ========== INDICATOR PAN HANDLERS ==========
  // These handle panning inside indicator charts and sync back to main chart
  const indicatorPanStartRef = useRef(null);
  const isIndicatorPanningRef = useRef(false);

  const handleIndicatorPanStart = useCallback((clientX, clientY) => {
    if (setting?.drawMode) return;

    isIndicatorPanningRef.current = true;
    indicatorPanStartRef.current = {
      x: clientX,
      y: clientY,
      xDomain: chartCanvasComponentRef.current?.state?.xScale?.domain()?.slice() || null
    };
  }, [setting?.drawMode]);

  const handleIndicatorPanMove = useCallback((clientX, clientY) => {
    if (!isIndicatorPanningRef.current || !indicatorPanStartRef.current) return;

    const deltaX = clientX - indicatorPanStartRef.current.x;

    // Calculate domain shift from pan delta and apply to main chart
    if (indicatorPanStartRef.current.xDomain && chartCanvasComponentRef.current?.state?.xScale) {
      const xScale = chartCanvasComponentRef.current.state.xScale;
      const [startDomain, endDomain] = indicatorPanStartRef.current.xDomain;

      const domainRange = endDomain - startDomain;
      const pixelRange = xScale.range()[1] - xScale.range()[0];
      const domainPerPixel = domainRange / pixelRange;

      // Mobile: Use positive deltaX, Desktop: Use negative deltaX
      const effectiveDelta = isMobileView ? deltaX : -deltaX;
      const domainShift = effectiveDelta * domainPerPixel;

      const newDomain = [startDomain + domainShift, endDomain + domainShift];

      // Apply to main chart
      if (chartCanvasComponentRef.current?.xAxisZoom) {
        chartCanvasComponentRef.current.xAxisZoom(newDomain);
      }

      // Sync all indicators using shared helper (proportional mapping + flushSync + lastMainDomainRef update)
      syncIndicatorsToMainDomain(newDomain);
    }
  }, [isMobileView, syncIndicatorsToMainDomain]);

  const handleIndicatorPanEnd = useCallback(() => {
    if (!isIndicatorPanningRef.current) return;
    isIndicatorPanningRef.current = false;
    indicatorPanStartRef.current = null;
  }, []);

  // Indicator mouse event handlers
  const onIndicatorMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    handleIndicatorPanStart(e.clientX, e.clientY);
  }, [handleIndicatorPanStart]);

  const onIndicatorMouseMove = useCallback((e) => {
    if (!isIndicatorPanningRef.current) return;
    handleIndicatorPanMove(e.clientX, e.clientY);
  }, [handleIndicatorPanMove]);

  const onIndicatorMouseUp = useCallback(() => {
    handleIndicatorPanEnd();
  }, [handleIndicatorPanEnd]);

  const onIndicatorMouseLeave = useCallback(() => {
    handleIndicatorPanEnd();
  }, [handleIndicatorPanEnd]);

  // Indicator touch event handlers
  const onIndicatorTouchStart = useCallback((e) => {
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    handleIndicatorPanStart(touch.clientX, touch.clientY);
  }, [handleIndicatorPanStart]);

  const onIndicatorTouchMove = useCallback((e) => {
    if (!isIndicatorPanningRef.current || e.touches.length !== 1) return;
    const touch = e.touches[0];
    handleIndicatorPanMove(touch.clientX, touch.clientY);
  }, [handleIndicatorPanMove]);

  const onIndicatorTouchEnd = useCallback(() => {
    handleIndicatorPanEnd();
  }, [handleIndicatorPanEnd]);
  // ========== END INDICATOR PAN HANDLERS ==========

  // Add window-level mouseup listener to handle cases where mouse leaves chart during drag
  useEffect(() => {
    const handleWindowMouseUp = () => {
      if (isPanningRef.current) {
        handleChartPanEnd();
      }
      if (isIndicatorPanningRef.current) {
        handleIndicatorPanEnd();
      }
    };
    window.addEventListener('mouseup', handleWindowMouseUp, { passive: true });
    return () => {
      window.removeEventListener('mouseup', handleWindowMouseUp);
    };
  }, [handleChartPanEnd, handleIndicatorPanEnd]);

  // The Y-axis price badge, fed from a MOBILE crosshair position.
  //
  // Client: "The mobile version isn't working correctly. It doesn't show the
  // Y-axis value... The desktop version seems fine."
  //
  // Right: the badge had no mobile source at all. The canvas-drawn
  // `MouseCoordinateY at="right"` was commented out when the badge became a CSS
  // div (so it could keep drawing over the empty area after the last candle),
  // and that div is fed only by updateChartHoverFromEvent — which is wired to
  // `onMouseMove`, and `onMouseMove` is explicitly `undefined` on mobile. So the
  // desktop gained a better badge and the mobile lost its only one.
  //
  // This is the same yScale.invert the desktop path uses, factored out so the
  // two cannot drift. `yLocal` is measured from chartContainerRef's top, which
  // is exactly the value the mobile crosshair already writes into the
  // horizontal line's `top` — so the badge can never disagree with the line it
  // is labelling, once the coordinate spaces are reconciled: see
  // CHART_MARGIN_TOP for why the container-relative y has to be shifted into
  // the plot area before yScale can read a price off it.
  const setMobileHoverPrice = useCallback((yLocal) => {
    const chartConfigs = chartCanvasComponentRef.current?.state?.chartConfigs;
    const mainChart = chartConfigs?.find((c) => c.id === 3);
    const yScale = mainChart?.yScale;
    if (!yScale) {
      setHoverPrice(null);
      return;
    }
    const price = yScale.invert(yLocal - CHART_MARGIN_TOP);
    if (price !== undefined && isFinite(price)) {
      setHoverPrice({ price, yPx: yLocal });
    } else {
      setHoverPrice(null);
    }
  }, []);

  // Simple mousedown to toggle crosshair and immediately show at touch position
  const handleToggleCrosshair = (e) => {
    console.log('[Crosshair] MouseDown - isMobileView:', isMobileView);
    if (!isMobileView) return;
    // While the drawing tool is open, a tap on the chart is a drawing action —
    // not a crosshair toggle. Keep the crosshair suppressed so its dashed lines
    // don't clutter the chart (and never get captured in a shared chart image).
    if (setting?.drawMode) return;

    // Get touch/click position
    const clientX = e.touches ? e.touches[0]?.clientX : e.clientX;
    const clientY = e.touches ? e.touches[0]?.clientY : e.clientY;

    setShowMobileCrosshair(prev => {
      const newValue = !prev;
      console.log('[Crosshair] Toggling:', prev, '->', newValue);

      // If activating crosshair, immediately update OHLC at touch position
      if (newValue && clientX) {
        // Store the touch position for immediate OHLC update
        lastPointerXRef.current = clientX;
        // Update immediately without delay - use flushSync in next tick
        // to ensure state is updated first but then update OHLC synchronously
        Promise.resolve().then(() => {
          flushSync(() => {
            updateOHLCFromScreenX(clientX);
          });
        });

        // Position the CSS-overlay crosshair directly here. The window-level
        // touchstart listener that normally does this checks showMobileCrosshair
        // via stale closure on the activating tap and bails out, so without
        // this the line wouldn't appear until the next finger movement.
        if (chartContainerRef.current && clientX != null && clientY != null) {
          const rect = chartContainerRef.current.getBoundingClientRect();
          const xLocal = clientX - rect.left;
          const yLocal = clientY - rect.top;
          if (xLocal >= 0 && xLocal <= rect.width &&
              yLocal >= 0 && yLocal <= rect.height) {
            if (indicatorCrosshairRef.current) {
              indicatorCrosshairRef.current.style.display = 'block';
              indicatorCrosshairRef.current.style.left = `${Math.round(xLocal)}px`;
            }
            if (horizontalCrosshairRef.current) {
              horizontalCrosshairRef.current.style.display = 'block';
              horizontalCrosshairRef.current.style.top = `${Math.round(yLocal)}px`;
            }
            // Same yLocal the line just used, so badge and line always agree.
            setMobileHoverPrice(yLocal);
          }
          // Set the date/time tooltip below the vertical line on activation.
          setMobileHoverDate(clientX);
        }
      }

      return newValue;
    });
  };
  // Keep the ref in sync so onChartTouchEnd can invoke the toggle without a TDZ.
  handleToggleCrosshairRef.current = handleToggleCrosshair;

  // Dispatch crosshair state changes to ChartTopSummary
  useEffect(() => {
    if (!isMobileView) return;

    // Only dispatch when crosshair state actually changes, not on data updates
    // Get the latest data point for initial OHLC values (only used when first activating)
    const latestData = initialData[initialData.length - 1];

    window.dispatchEvent(new CustomEvent('crosshairStateChange', {
      detail: {
        isActive: showMobileCrosshair,
        ohlcData: latestData ? {
          open: latestData.open,
          high: latestData.high,
          low: latestData.low,
          close: latestData.close
        } : null
      }
    }));

    // Hide CSS-overlay crosshair (and clear the date tooltip) when toggled off on mobile.
    if (!showMobileCrosshair) {
      if (indicatorCrosshairRef.current) indicatorCrosshairRef.current.style.display = 'none';
      if (horizontalCrosshairRef.current) horizontalCrosshairRef.current.style.display = 'none';
      setChartHoverInfo(null);
      // The badge is part of the crosshair; it goes away with it.
      setHoverPrice(null);
    }
  }, [showMobileCrosshair, isMobileView]); // Removed initialData dependency

  // Opening the drawing tool must disable the mobile crosshair. Historically the
  // crosshair was off while drawing; without this its dashed lines linger over
  // the chart (and get captured in a shared chart image). Closing the tool
  // restores normal tap-to-toggle behavior via handleToggleCrosshair.
  useEffect(() => {
    if (!setting?.drawMode) return;
    setShowMobileCrosshair(false);
    if (indicatorCrosshairRef.current) indicatorCrosshairRef.current.style.display = 'none';
    if (horizontalCrosshairRef.current) horizontalCrosshairRef.current.style.display = 'none';
    setChartHoverInfo(null);
    setHoverPrice(null);
  }, [setting?.drawMode]);

  const getChartMainItemPosition = () => {
    let position = {
      top: "",
      left: "",
    };

    const chartDiv = document.querySelector(".chart-item.chart-main");

    if (chartDiv) {
      // Get the bounding rectangle of the element
      const rect = chartDiv.getBoundingClientRect();

      // Extract the position
      position = {
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
      };
    } else {
      console.log("Element not found");
    }

    return position;
  };

const isBTCUSD = (symbol || "").toUpperCase() === "X:BTCUSD";

// Count indicators that appear below the chart
const belowIndicatorCount = useMemo(() => {
  if (!Array.isArray(indicatorConfig)) return 0;
  return indicatorConfig.filter(ind => ind.position === "below" && ind.selectValue !== "").length;
}, [indicatorConfig]);

// Count indicators that appear above the chart (used for height math so the
// main chart shrinks the same way it does for below indicators).
const aboveIndicatorCount = useMemo(() => {
  if (!Array.isArray(indicatorConfig)) return 0;
  return indicatorConfig.filter(ind => ind.position === "above" && ind.selectValue !== "").length;
}, [indicatorConfig]);

// Stabilize dimensions using useMemo to prevent jumps on re-render
// Height shrinks when indicators are added below (like TradingView)
const { height, width, indicatorPanelHeight } = useMemo(() => {
  // Base height percentages
  const baseHeightPercent = isMobileView ? 0.76 : 0.80;

  // `/<slug>/trade` is included here for the same reason it is included in
  // isFullScreenChartRoute: with Full Screen now working on the League Trade
  // screen, that screen hides the dashboard header exactly like /charts, so it
  // has to take the same height arithmetic. Left out, the chart would keep
  // reserving 74px for a header that is no longer on screen.
  const isDesktopChartsPath =
    (location.pathname === `/charts` ||
      location.pathname.startsWith("/chartSearch/") ||
      /^\/[^/]+\/trade$/.test(location.pathname)) &&
    !isMobileView;

  // Header hidden on desktop /charts when sidebar is collapsed (see char.jsx)
  const isDynamicRouteEarly = /\/[^/]+\/(buysell|portfolio|leaderboard|profile|charts|feeds|chatHistory|create-feed|edit-feed|followers)/.test(location.pathname);
  const isChartsRouteEarly = isFullScreenChartRoute(location.pathname);
  const overrideEarly = getSidebarCollapseOverride();
  const sidebarCollapsedEarly = isChartsRouteEarly
    ? (overrideEarly === null ? false : overrideEarly)
    : false;
  const chartsHeaderHidden = isDesktopChartsPath && sidebarCollapsedEarly;

  // Calculate available height for main chart
  // On mobile: total available = 100vh - header(56px) - bottom navbar(64px) - toolbar(50px) = ~calc(100vh - 170px),
  //   plus back whatever of those bottom bars is currently hidden (keyboard up
  //   with the symbol search open hides both) — see reclaimedBottomChrome above.
  // On desktop /charts: subtract top toolbar (~54px) + bottom bar (~50px) + header (74, or 0 when hidden)
  // + 8px safety buffer so chart always fits inside flex slot container and X-axis labels aren't clipped.
  const totalAvailable = isMobileView
    ? viewportSize.height - 170 + reclaimedBottomChrome
    : isDesktopChartsPath
    ? viewportSize.height - (chartsHeaderHidden ? 0 : 74) - 54 - 50 - 8
    : viewportSize.height * baseHeightPercent;

  // Each indicator panel takes a fixed slice so math stays consistent between
  // totalAvailable subtraction and actual indicator <ChartCanvas height=...> render.
  // Both above- and below-positioned indicators consume the same height, so the
  // main chart must shrink for either kind to avoid clipping the bottom.
  const indicatorPanelHeight = isMobileView ? 150 : 180;
  const totalIndicatorHeight = (belowIndicatorCount + aboveIndicatorCount) * indicatorPanelHeight;

  // Shrink main chart height when indicators are added
  let h = totalAvailable - totalIndicatorHeight;

  // Minimum height for main chart
  const minHeight = isMobileView ? 200 : 300;
  h = Math.max(h, minHeight);

  const isDynamicRoute = isDynamicRouteEarly;
  const sidebarCollapsed = sidebarCollapsedEarly;
  const isLeagueDesktopTradePath = /^\/[^/]+\/trade$/.test(location.pathname);
  const isChartsPath = location.pathname === `/charts`;
  const isChartSearchPath = location.pathname.startsWith(`/chartSearch/`);
  const isChartLikePath = isChartsPath || isChartSearchPath;
  // The League Trade screen can now collapse the sidebar too (its Full Screen
  // button was inert before — see isFullScreenChartRoute). When it does, the
  // sidebar is hidden ENTIRELY, exactly as on /charts, so it takes /charts'
  // collapsed width and not the 80px rail width, which would have left the plot
  // 93px narrower than the space it actually has. Uncollapsed (275px) is
  // unchanged, so nothing moves until the button is pressed.
  const sidebarWidth = sidebarCollapsed
    ? (isChartLikePath || isLeagueDesktopTradePath ? -13 : 80)
    : 275;

  let w = viewportSize.width - sidebarWidth - 15 - 100;

  if (
    location.pathname !== `/buysell` &&
    location.pathname !== `/charts` &&
    !isChartSearchPath
  ) {
    w = viewportSize.width - 80 - 326 - 100;
  }

  // Client Rev 3, 7: "The desktop Trade chart inside a league is still cutting
  // off chart bars. The league desktop Trade chart should function and visually
  // behave the same as the main site's desktop Trade chart."
  //
  // It was taking the OLD width formula. /<slug>/trade is neither `/charts` nor
  // `/chartSearch/…`, so it fell through to the generic branch above —
  //     w = innerWidth - 80 - 326 - 100
  // which at 1536px is 1030px, while the new desktop layout it actually renders
  // in only has room for 921px. Measured at 1536x900 BEFORE: main /charts plot
  // 921px wide inside a 912px container; league /<slug>/trade plot 1030px inside
  // a 904px container. The 109px of extra plot pushed the whole price axis and
  // the newest candles past the container's overflow:hidden edge — the axis was
  // not drawn at all on the league screen, and the last bars were sliced.
  //
  // The screen uses the same chrome as /charts (48px drawing rail on the left,
  // 288px watchlist on the right — visible in both screenshots), so it takes the
  // same measurement. Matched to /trade only, not to every /<slug>/ route:
  // `isChartLikePath` also decides `sidebarWidth` above, and widening it there
  // would change /<slug>/charts, which the client did not report.
  const isLeagueDesktopTrade = isLeagueDesktopTradePath;

  // New desktop /charts layout has left drawing rail (48px) and right watchlist (288px).
  // Extra 90px buffer keeps price axis labels from clipping against the watchlist edge.
  if ((isChartLikePath || isLeagueDesktopTrade) && viewportSize.width >= 768) {
    w = viewportSize.width - sidebarWidth - 48 - 288 - 4;
  }

  if (viewportSize.width < 768) {
    w = viewportSize.width - 15;
  }

  // For X:BTCUSD on mobile, use near-full width
  if (isBTCUSD && viewportSize.width < 768) {
    w = viewportSize.width - 8;
  }

  // Mobile chart share: fill the export edge to edge (see squareExport.js).
  // `adjust` is the leftover the share flow measured on the first square pass.
  if (squareExport && isMobileView) {
    const panelCount = belowIndicatorCount + aboveIndicatorCount;
    const panel = squareExportPanelHeight(squareExport.height, panelCount, indicatorPanelHeight);
    return {
      height: squareExport.height - panelCount * panel + squareExport.adjust,
      width: squareExport.width,
      indicatorPanelHeight: panel,
    };
  }

  return { height: h, width: w, indicatorPanelHeight };
}, [isMobileView, viewportSize, reclaimedBottomChrome, location.pathname, isBTCUSD, belowIndicatorCount, aboveIndicatorCount, sidebarCollapsedTick, squareExport]);

  // Increased right margin to prevent label cutoff
  // Check if we have actual data before processing
  const hasValidInitialData = Array.isArray(initialData) && initialData.length > 0;

  // Track if we're in a transition state (symbol/period changed but data hasn't arrived yet)
  const isDataTransitioning = !hasValidInitialData || data.length === 0;

  // Lock the chart to horizontal pan + zoom only — no up/down movement.
  // `touch-action: none` / `overscroll-behavior` (set in CSS) are ignored by
  // iOS Safari for momentum scroll, so a flick up/down still lets the page
  // rubber-band/drift behind the chart (the "hanging" the user reported).
  // React's onTouchMove is registered passive and cannot cancel that, so we
  // attach our own NON-passive listener and preventDefault the native scroll.
  // The chart's left/right pan + pinch-zoom are driven by the library's own JS
  // handlers (they read touch coordinates, not native scrolling), so they keep
  // working — only the browser's default scroll/zoom is suppressed.
  // NOTE: keyed on isDataTransitioning, not [] — the chart container is mounted
  // behind a "Loading…" early return, so a mount-only effect would run while
  // chartContainerRef is still null and never attach. Re-running when the chart
  // actually appears (isDataTransitioning -> false) guarantees attachment.
  useEffect(() => {
    if (isDataTransitioning) return;
    const chartContainer = chartContainerRef.current;
    if (!chartContainer) return;

    const preventTouchScroll = (e) => {
      // Let taps on the in-chart zoom / refresh controls behave normally.
      if (e.target?.closest?.('.chart-zoom-btn')) return;
      if (e.cancelable) e.preventDefault();
    };

    chartContainer.addEventListener('touchmove', preventTouchScroll, { passive: false });
    return () => {
      chartContainer.removeEventListener('touchmove', preventTouchScroll);
    };
  }, [isDataTransitioning]);

  // Update viewport span when data changes (must be after data is defined)
  useEffect(() => {
    if (!chartCanvasComponentRef.current) return;
    const domain = chartCanvasComponentRef.current.state?.xScale?.domain();
    updateViewportSpanFromDomain(domain);
  }, [data, updateViewportSpanFromDomain]);

  const pricesDisplayFormat = (value) => {
    if (value == null) return "";
    const num = Number(value);
    if (Number.isFinite(num)) {
      const absNum = Math.abs(num);
      // Show decimals only if not a whole number
      return absNum % 1 === 0 ? absNum.toString() : absNum.toFixed(1);
    }
    // Fallback: remove any negative signs from string
    return String(value).replace(/^\s*-\s*/, "").replace(/^\s*−\s*/, "");
  };

  // Use hasValidInitialData for early return check (not processed data)
  const hasData = data && data.length > 0; // Keep this for any legacy references
  const max = hasData ? xAccessor(data[data.length - 1]) : new Date();
  const min = hasData ? xAccessor(data[0]) : new Date();
  const domainIsDate = min instanceof Date;
  const numericMin = domainIsDate ? min.getTime() : min;
  const numericMax = domainIsDate ? max.getTime() : max;
  const toDomainValue = useCallback(
    (value) => (domainIsDate ? new Date(value) : value),
    [domainIsDate]
  );

  // Detect when historical data is prepended (vs appended from live updates)
  const currentDataLength = data.length;
  const prevLength = prevDataLengthRef.current;
  const barsAdded = currentDataLength - prevLength;

  // Get first bar's date to detect if data was prepended or appended
  const currentFirstBarDate = data.length > 0 ? data[0].date?.getTime() : null;
  const prevFirstBarDate = prevFirstBarDateRef.current;

  // Historical data PREPEND: data length increased AND first bar's date changed (is earlier)
  // This distinguishes from live data APPEND where first bar stays the same but new bars are added at end
  const wasDataPrepended = barsAdded > 0 && prevLength > 0 &&
    prevFirstBarDate !== null && currentFirstBarDate !== null &&
    currentFirstBarDate < prevFirstBarDate; // New first bar is OLDER = prepend

  // Use preservedDomainRef if available (set in onLoadBefore), otherwise fall back to lastXExtentsRef
  const savedViewport = preservedDomainRef.current || lastXExtentsRef.current;
  const isHistoricalPrepend = wasDataPrepended && hasInitializedRef.current && savedViewport;

  // xExtents logic: show most recent bars on initial load to allow scrolling left for historical data
  let xExtents;
  if (data.length === 0) {
    // No data yet - don't set xExtents, will show loading state
    xExtents = undefined;
  
  } else if (justChangedIntervalRef.current && data.length > 0) {
    // Period/symbol just changed - ALWAYS show most recent data regardless of cached state
    // This is crucial for fixing the monthly->weekly/daily navigation bug
    const visibleBars = isMobileView ? 50 : 100;
    const safeMax = Number.isFinite(max) ? max : data.length - 1;
    xExtents = [Math.max(0, safeMax - visibleBars), safeMax];
    lastXExtentsRef.current = xExtents;
    hasInitializedRef.current = true;
    prevDataLengthRef.current = currentDataLength;
    prevFirstBarDateRef.current = currentFirstBarDate; // Track first bar date
  
  } else if (!hasInitializedRef.current && data.length > 0) {
    // Initial load - show MOST RECENT bars (from right side) so user can pan left to load historical
    const visibleBars = isMobileView ? 50 : 100;
    const safeMin = Number.isFinite(min) ? min : 0;
    const safeMax = Number.isFinite(max) ? max : data.length - 1;
    xExtents = [Math.max(safeMin, safeMax - visibleBars), safeMax];
    lastXExtentsRef.current = xExtents;
    hasInitializedRef.current = true;
    prevDataLengthRef.current = currentDataLength;
    prevFirstBarDateRef.current = currentFirstBarDate; // Track first bar date
    console.log('[Chart] Initial load showing recent bars, extent:', xExtents);
  } else if (isHistoricalPrepend) {
    // Use preservedDomainRef if available (more accurate as it captures actual viewport at scroll time)
    // Otherwise fall back to lastXExtentsRef
    const [prevStart, prevEnd] = savedViewport;

    // SHIFT domain by number of prepended bars to maintain the same visual position
    const shiftedStart = prevStart + barsAdded;
    const shiftedEnd = prevEnd + barsAdded;

    xExtents = [shiftedStart, shiftedEnd];
    lastXExtentsRef.current = xExtents;

    preservedDomainRef.current = null; // Clear after use
    prevDataLengthRef.current = currentDataLength;
    prevFirstBarDateRef.current = currentFirstBarDate; // Track new first bar date
  } else if (lastXExtentsRef.current && data.length > 0) {
    // Use cached xExtents but validate they're still within data bounds
    const [cachedMin, cachedMax] = lastXExtentsRef.current;
    const safeMax = Number.isFinite(max) ? max : data.length - 1;
    if (cachedMax <= safeMax) {
      xExtents = lastXExtentsRef.current;
    } else {
      // Cached extents are out of bounds, recalculate
      const visibleBars = isMobileView ? 50 : 100;
      xExtents = [Math.max(0, safeMax - visibleBars), safeMax];
      lastXExtentsRef.current = xExtents;
    }
    // Always keep refs in sync
    if (currentDataLength !== prevDataLengthRef.current) {
      prevDataLengthRef.current = currentDataLength;
      prevFirstBarDateRef.current = currentFirstBarDate;
    }
  } else if (data.length > 0) {
    // Fallback: calculate fresh xExtents
    const visibleBars = isMobileView ? 50 : 100;
    const safeMax = Number.isFinite(max) ? max : data.length - 1;
    xExtents = [Math.max(0, safeMax - visibleBars), safeMax];
    lastXExtentsRef.current = xExtents;
    prevDataLengthRef.current = currentDataLength;
    prevFirstBarDateRef.current = currentFirstBarDate;
  }

  const [legendItems, setLegendItems] = useState([]);
  const [updatedLegendItems, setUpdatedLegendItems] = useState([]);

  // DISABLED: Auto-jump to historical bars - let user manually scroll to see them
  // useEffect(() => {
  //   if (!dataIncreased || !chartCanvasComponentRef.current) return;
  //   const span = lastViewportSpanRef.current ?? getDefaultViewportSpan();
  //   const maxSpan = Math.max(1, numericMax - numericMin);
  //   const targetSpan = Math.min(Math.max(1, span), maxSpan);
  //   const targetStart = numericMin;
  //   const targetEnd = Math.min(numericMin + targetSpan, numericMax);

  //   try {
  //     chartCanvasComponentRef.current.xAxisZoom([
  //       toDomainValue(targetStart),
  //       toDomainValue(targetEnd),
  //     ]);
  //   } catch (error) {
  //     console.debug("[Chart] align left skipped:", error);
  //   }
  // }, [dataIncreased, getDefaultViewportSpan, numericMax, numericMin, toDomainValue]);
  const isChartsRoute = location.pathname === "/charts";

// Memoize chart extents functions to prevent recreation on every render
const barChartExtents = useCallback((d) => {
  const v = Number(d?.volume);
  return Number.isFinite(v) ? v : 0;
}, []);

const candleChartExtents = useCallback((d) => {
  // 🔒 HARD SAFETY: react-financial-charts can pass undefined
  if (!d) {
    return [0, 1];
  }

  // Normal single-datum case (this is what RFC uses)
  if (typeof d === "object" && !Array.isArray(d)) {
    const high = Number(d.high);
    const low = Number(d.low);

    if (Number.isFinite(high) && Number.isFinite(low)) {
      const range = high - low || 1;
      return [
        high + range * 0.02,
        low - range * 0.02,
      ];
    }

    return [0, 1];
  }

  // Fallback (should almost never be used)
  if (Array.isArray(d)) {
    const highs = d.map(x => x?.high).filter(Number.isFinite);
    const lows  = d.map(x => x?.low).filter(Number.isFinite);

    if (highs.length && lows.length) {
      const max = Math.max(...highs);
      const min = Math.min(...lows);
      const range = max - min || 1;

      return [
        max + range * 0.02,
        min - range * 0.02,
      ];
    }
  }

  return [0, 1];
}, []);

// Calculates y-domain from ALL visible data so padding scales with the chart range,
// not individual candle ranges. Ensures 1-2 tick labels above the highest point.
const candleChartExtentsCalculator = useCallback(({ plotData }) => {
  if (!plotData || plotData.length === 0) return [0, 1];

  let maxHigh = -Infinity;
  let minLow = Infinity;

  for (const d of plotData) {
    if (!d) continue;
    const h = Number(d.high);
    const l = Number(d.low);
    if (Number.isFinite(h) && h > maxHigh) maxHigh = h;
    if (Number.isFinite(l) && l < minLow) minLow = l;
  }

  if (!Number.isFinite(maxHigh) || !Number.isFinite(minLow)) return [0, 1];

  const range = maxHigh - minLow || 1;

  // 8% of visible range above, 2% below — keeps chart large while
  // providing breathing room above the highest point.
  // Return order must be [min, max] to match d3 scale domain convention.
  return [minLow - range * 0.02, maxHigh + range * 0.08];
}, []);


// Memoize accessor functions to prevent unnecessary re-renders
const volumeColor = useCallback((d) => {
  return d.close > d.open
    ? "rgba(38, 166, 154, 0.3)"
    : "rgba(239, 83, 80, 0.3)";
}, []);

const volumeSeries = useCallback((d) => {
  return d.volume;
}, []);

  const calculateADL = useCallback((chartData) => {
    let adl = 0; // Start with an ADL of 0
    return chartData.map((datum) => {
      const { close, high, low, volume } = datum;

      // A bar that opened, traded and closed at one price has high === low, so
      // the Money Flow Multiplier's denominator is zero. Dividing by it yields
      // NaN (or ±Infinity when the numerator is non-zero), and because ADL is a
      // RUNNING TOTAL that poison spreads to every later bar — one flat bar
      // kills the rest of the series, the y-extent becomes NaN, and the panel
      // renders with d3's fallback 0..1 domain and no line at all.
      //
      // That is the client's "data drops off on the 4 hour and down... if you
      // scroll left you'll eventually see the ADL": the bars BEFORE the first
      // flat one are fine, everything after is gone. Zero-range bars are
      // routine intraday and essentially absent on daily and above, which is
      // exactly the split they reported between timeframes and devices.
      //
      // The standard definition treats a zero-range bar as contributing no
      // accumulation or distribution, so the multiplier is 0, not undefined.
      const range = high - low;
      const moneyFlowMultiplier = range === 0 ? 0 : (close - low - (high - close)) / range;
      const moneyFlowVolume = moneyFlowMultiplier * (volume || 0);

      // Belt and braces: a malformed bar (missing OHLC, non-numeric volume)
      // must not be able to reintroduce the same running-total poisoning.
      if (Number.isFinite(moneyFlowVolume)) {
        adl += moneyFlowVolume; // Accumulate the Money Flow Volume to get the ADL
      }

      return {
        ...datum,
        adl, // Add the ADL to the datum
      };
    });
  }, []);

  // Memoize dataWithADL to prevent recalculation on every render
  const dataWithADL = useMemo(() => calculateADL(data), [data, calculateADL]);

  // Track the last hovered bar index and its OHLC snapshot to avoid unnecessary updates
  const lastHoveredIndexRef = useRef(-1);
  const lastOhlcSnapshotRef = useRef(null);
  const initialDataRef = useRef(initialData);
  const xAccessorRef = useRef(xAccessor);
  const lastPointerXRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  // Keep refs updated with latest values
  useEffect(() => {
    initialDataRef.current = initialData;
    xAccessorRef.current = xAccessor;
  }, [initialData, xAccessor]);

  // Reset hovered index when crosshair is toggled
  useEffect(() => {
    if (showMobileCrosshair) {
      lastHoveredIndexRef.current = -1;
      lastOhlcSnapshotRef.current = null;
      lastPointerXRef.current = null;
      console.log('[Crosshair] Reset hovered index');
    }
  }, [showMobileCrosshair]);

  // Reset hovered index when symbol/period changes
  useEffect(() => {
    console.log('[Crosshair] Symbol/period changed, resetting hovered index');
    lastHoveredIndexRef.current = -1;
    lastPointerXRef.current = null; // Clear hover state
  }, [symbol, intervalKey]);

  // Initialize OHLC with latest bar ONLY when first loading or symbol changes
  // Do NOT update when WebSocket sends new data (initialData changes)
  useEffect(() => {
    // Only initialize if user is not currently hovering over a bar
    if (initialData && initialData.length > 0 && lastPointerXRef.current === null) {
      const latestBar = initialData[initialData.length - 1];
      const ohlcData = {
        open: latestBar.open,
        high: latestBar.high,
        low: latestBar.low,
        close: latestBar.close
      };
      setCurrentOHLC(ohlcData);
      // Also dispatch to ChartTopSummary for both mobile and desktop
      window.dispatchEvent(new CustomEvent('crosshairDataChange', {
        detail: { ohlcData }
      }));
      console.log('[Crosshair] Initialized OHLC with latest bar (user not hovering):', latestBar);
    }
  }, [symbol, intervalKey]); // Removed initialData and isMobileView dependency to prevent WebSocket updates

  // Listen for OHLC updates and store in local state for Legend1 display
  useEffect(() => {
    const handleOHLCUpdate = (event) => {
      const { ohlcData } = event.detail;
      if (ohlcData) {
        setCurrentOHLC(ohlcData);
      }
    };

    window.addEventListener('crosshairDataChange', handleOHLCUpdate);

    return () => {
      window.removeEventListener('crosshairDataChange', handleOHLCUpdate);
    };
  }, []);

  // Update the bottom-of-stack date/time overlay from any mouse-move event.
  // Works whether the cursor is on the main price panel or any indicator panel,
  // since they all share the main chart's xScale (positions are equivalent in x).
  const updateChartHoverFromEvent = useCallback((e) => {
    if (!chartCanvasComponentRef.current || !chartContainerRef.current) return;
    const rect = chartContainerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (x < 0 || x > rect.width) {
      setChartHoverInfo(null);
      if (indicatorCrosshairRef.current) {
        indicatorCrosshairRef.current.style.display = 'none';
      }
      if (horizontalCrosshairRef.current) {
        horizontalCrosshairRef.current.style.display = 'none';
      }
      return;
    }

    // ── Draw crosshair lines FIRST, regardless of whether a data bar exists ──
    // This ensures lines extend to the chart edges (empty right area after last candle).
    if (indicatorCrosshairRef.current) {
      indicatorCrosshairRef.current.style.display = 'block';
      indicatorCrosshairRef.current.style.left = `${Math.round(x)}px`;
    }

    // Horizontal line follows cursor Y across the entire outer wrapper.
    // Also compute price from yScale at cursor position for the custom price badge.
    if (e.currentTarget) {
      const outerRect = e.currentTarget.getBoundingClientRect();
      const yInOuter = e.clientY - outerRect.top;
      if (horizontalCrosshairRef.current) {
        if (yInOuter >= 0 && yInOuter <= outerRect.height) {
          horizontalCrosshairRef.current.style.display = 'block';
          horizontalCrosshairRef.current.style.top = `${Math.round(yInOuter)}px`;
        } else {
          horizontalCrosshairRef.current.style.display = 'none';
        }
      }

      // ── Custom price badge: calculate price from yScale at cursor Y ──
      // yScale maps from the main-chart coordinate space, so we need the Y
      // relative to the main chart canvas (not the outer wrapper).
      const chartState = chartCanvasComponentRef.current?.state;
      const chartConfigs = chartState?.chartConfigs;
      let yScale = null;
      if (chartConfigs) {
        const mainChart = chartConfigs.find(c => c.id === 3);
        if (mainChart) yScale = mainChart.yScale;
      }
      if (yScale && chartContainerRef.current) {
        // The main chart canvas starts at the top of chartContainerRef + margin.top,
        // so the container-relative y has to come down into the plot area before
        // yScale can read a price off it — see CHART_MARGIN_TOP.
        const containerRect = chartContainerRef.current.getBoundingClientRect();
        const yInContainer = e.clientY - containerRect.top;
        const price = yScale.invert(yInContainer - CHART_MARGIN_TOP);
        if (price !== undefined && isFinite(price)) {
          setHoverPrice({ price, yPx: yInOuter });
        } else {
          setHoverPrice(null);
        }

        // Hide mouse pointer arrow when cursor is over the Y-axis price block area
        // (horizontal crosshair line still points to exact level, so price is not obstructed)
        if (containerRect) {
          const xInContainer = e.clientX - containerRect.left;
          // Threshold extended by 12px to cover the full black price box rectangle and all text
          const yAxisThreshold = containerRect.width - (margin.right || 40) - 12;
          const isOverY = xInContainer >= yAxisThreshold;
          setIsOverYAxis(isOverY);
        }
      } else {
        setHoverPrice(null);
        setIsOverYAxis(false);
      }
    }

    // ── Date badge: only update when hovering over a valid bar ──
    const xScale = chartCanvasComponentRef.current.state?.xScale;
    if (!xScale) return;
    const currentData = initialDataRef.current;
    if (!currentData || currentData.length === 0) return;
    const barIndex = Math.round(xScale.invert(x));
    const idx = Math.max(0, Math.min(currentData.length - 1, barIndex));
    const bar = currentData[idx];
    const dateValue = bar?.date instanceof Date ? bar.date : new Date(bar?.timestamp || bar?.date);
    if (!dateValue || isNaN(dateValue.getTime())) {
      setChartHoverInfo(null);
      return;
    }
    setChartHoverInfo({ x, dateStr: timeFormat("%Y-%m-%d %H:%M")(dateValue) });
  }, []);

  const clearChartHover = useCallback((e) => {
    setChartHoverInfo(null);
    setHoverPrice(null);
    setIsOverYAxis(false);
    if (indicatorCrosshairRef.current) {
      indicatorCrosshairRef.current.style.display = 'none';
    }
    if (horizontalCrosshairRef.current) {
      horizontalCrosshairRef.current.style.display = 'none';
    }
  }, []);

  // Mobile equivalent of the chartHoverInfo update inside updateChartHoverFromEvent.
  // Sets the date/time tooltip at the bottom of the vertical crosshair line so it
  // shows up on touch interactions (the desktop path is mousemove-driven).
  const setMobileHoverDate = useCallback((clientX) => {
    if (!chartCanvasComponentRef.current || !chartContainerRef.current) return;
    const rect = chartContainerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    if (x < 0 || x > rect.width) return;
    const xScale = chartCanvasComponentRef.current.state?.xScale;
    if (!xScale) return;
    const currentData = initialDataRef.current;
    if (!currentData || currentData.length === 0) return;
    const barIndex = Math.round(xScale.invert(x));
    const idx = Math.max(0, Math.min(currentData.length - 1, barIndex));
    const bar = currentData[idx];
    const dateValue = bar?.date instanceof Date ? bar.date : new Date(bar?.timestamp || bar?.date);
    if (!dateValue || isNaN(dateValue.getTime())) return;
    setChartHoverInfo({ x, dateStr: timeFormat("%Y-%m-%d %H:%M")(dateValue) });
  }, []);

  // Function to calculate and dispatch OHLC based on screen X position
  const updateOHLCFromScreenX = useCallback((screenX) => {
    if (!chartCanvasComponentRef.current || !chartContainerRef.current) {
      return;
    }

    const rect = chartContainerRef.current.getBoundingClientRect();
    const x = screenX - rect.left;

    // Try to get the xScale from the chart - get fresh reference each time
    const xScale = chartCanvasComponentRef.current.state?.xScale;
    if (!xScale) {
      return;
    }

    // Invert the x position to get the data index directly
    // xScale.invert returns the domain value (which is the bar index in discontinuous scale)
    const invertedValue = xScale.invert(x);

    // Round to nearest integer bar index
    const barIndex = Math.round(invertedValue);

    // Use current refs for latest data
    const currentData = initialDataRef.current;
    if (!currentData || currentData.length === 0) {
      return;
    }

    // Clamp barIndex to valid range
    const closestIndex = Math.max(0, Math.min(currentData.length - 1, barIndex));

    // Always update to ensure we get the bar at the crosshair position
    // (especially important during mobile scroll when chart pans under finger)
    lastHoveredIndexRef.current = closestIndex;

    const hoveredData = currentData[closestIndex];
    if (hoveredData) {
      // Create a snapshot of OHLC values at this moment
      const ohlcSnapshot = {
        open: hoveredData.open,
        high: hoveredData.high,
        low: hoveredData.low,
        close: hoveredData.close
      };
      lastOhlcSnapshotRef.current = ohlcSnapshot;

      // Use flushSync to force immediate synchronous update - no batching delay
      // This is crucial for mobile scroll to show accurate real-time values
      flushSync(() => {
        setCurrentOHLC(ohlcSnapshot);
      });

      // Also dispatch event for other components (mobile header)
      window.dispatchEvent(new CustomEvent('crosshairDataChange', {
        detail: {
          ohlcData: ohlcSnapshot
        }
      }));
    }
  }, []); // Empty deps - uses refs which are always current

  // Track mouse/touch movement AND use polling to update when chart pans
  // Works for both mobile (when crosshair active) and desktop (always)
  useEffect(() => {
    if (!chartContainerRef.current) {
      return;
    }

    const container = chartContainerRef.current;

    const handlePointerMove = (e) => {
      if (isMobileView) return; // Desktop only

      // Check if mouse is within chart bounds
      const rect = container.getBoundingClientRect();
      const clientX = e.clientX;
      const clientY = e.clientY;

      // Only process if mouse is within chart container
      if (clientX >= rect.left && clientX <= rect.right &&
          clientY >= rect.top && clientY <= rect.bottom) {
        lastPointerXRef.current = clientX;
        updateOHLCFromScreenX(clientX);
      }
    };

    // Position the CSS-overlay crosshair at the given client coords. The vertical
    // line spans the full chart+indicator stack; the horizontal line is scoped
    // to the main chart's bounds (rect = chartContainerRef rect).
    const positionMobileCrosshair = (clientX, clientY, rect) => {
      const xLocal = clientX - rect.left;
      const yLocal = clientY - rect.top;
      if (indicatorCrosshairRef.current) {
        indicatorCrosshairRef.current.style.display = 'block';
        indicatorCrosshairRef.current.style.left = `${Math.round(xLocal)}px`;
      }
      if (horizontalCrosshairRef.current) {
        horizontalCrosshairRef.current.style.display = 'block';
        horizontalCrosshairRef.current.style.top = `${Math.round(yLocal)}px`;
      }
      setMobileHoverPrice(yLocal);
    };

    // Handle touch events for mobile - update immediately on every touch move
    const handleTouchMove = (e) => {
      if (!isMobileView || !showMobileCrosshairRef.current) return;

      const touch = e.touches[0];
      if (!touch) return;

      const rect = container.getBoundingClientRect();
      const clientX = touch.clientX;
      const clientY = touch.clientY;

      // Only process if touch is within chart container
      if (clientX >= rect.left && clientX <= rect.right &&
          clientY >= rect.top && clientY <= rect.bottom) {
        lastPointerXRef.current = clientX;
        // Update immediately - no delay, no RAF
        // This fires on every touchmove event for maximum responsiveness
        updateOHLCFromScreenX(clientX);
        positionMobileCrosshair(clientX, clientY, rect);
        setMobileHoverDate(clientX);
      }
    };

    // Handle touchstart to immediately position crosshair at touch point
    const handleTouchStart = (e) => {
      if (!isMobileView || !showMobileCrosshairRef.current) return;

      const touch = e.touches[0];
      if (!touch) return;

      const rect = container.getBoundingClientRect();
      const clientX = touch.clientX;
      const clientY = touch.clientY;

      // Only process if touch is within chart container
      if (clientX >= rect.left && clientX <= rect.right &&
          clientY >= rect.top && clientY <= rect.bottom) {
        lastPointerXRef.current = clientX;
        updateOHLCFromScreenX(clientX);
        positionMobileCrosshair(clientX, clientY, rect);
        setMobileHoverDate(clientX);
      }
    };

    // Handle mouse leaving the chart - keep the last OHLC displayed
    const handleMouseLeave = () => {
      if (!isMobileView) {
        lastPointerXRef.current = null;
        // Don't reset OHLC - keep showing the last hovered bar's values
      }
    };

    // For wheel events (scrolling/zooming)
    const handleWheel = (e) => {
      const rect = container.getBoundingClientRect();
      const clientX = e.clientX;
      const clientY = e.clientY;

      // Only process if wheel is within chart
      if (clientX >= rect.left && clientX <= rect.right &&
          clientY >= rect.top && clientY <= rect.bottom) {
        lastPointerXRef.current = clientX;
        // Small delay to let chart pan complete
        setTimeout(() => {
          if (lastPointerXRef.current !== null) {
            updateOHLCFromScreenX(lastPointerXRef.current);
          }
        }, 10);
      }
    };

    // Use window-level listeners to ensure we capture all mouse movements
    // This bypasses any event blocking by the chart canvas
    window.addEventListener('mousemove', handlePointerMove, { passive: true });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('wheel', handleWheel, { passive: true });
    container.addEventListener('mouseleave', handleMouseLeave, { passive: true });

    // Start continuous polling - updates OHLC as chart pans under the crosshair
    // This is crucial for mobile: as you drag and the chart pans, the same finger position
    // points to different bars - we need to keep recalculating which bar is at that X position
    let rafId = null;

    const pollOHLC = () => {
      if (lastPointerXRef.current !== null) {
        // Get fresh xScale and recalculate which bar is at the stored X position
        updateOHLCFromScreenX(lastPointerXRef.current);
      }
      rafId = requestAnimationFrame(pollOHLC);
    };

    rafId = requestAnimationFrame(pollOHLC);

    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('wheel', handleWheel);
      container.removeEventListener('mouseleave', handleMouseLeave);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isMobileView, updateOHLCFromScreenX, initialData, setMobileHoverPrice]);

  // Window-level capture-phase tap detector for the mobile crosshair toggle.
  // Capture phase fires before any descendant (including the chart canvas)
  // can stopPropagation, guaranteeing we see every touchstart/move/end while
  // the touch is on the chart. The toggle only fires when the touch barely
  // moved (<=5px) AND was short (<=300ms) — i.e. an unambiguous tap.
  useEffect(() => {
    if (!isMobileView) return;

    const handleTapStart = (e) => {
      if (e.touches.length !== 1) {
        tapTrackingRef.current.isTap = false;
        return;
      }
      const touch = e.touches[0];
      // Only count touches that began inside the chart container, and not on
      // an interactive child like a zoom button.
      const target = e.target;
      if (target && target.closest && target.closest('.chart-zoom-btn')) {
        tapTrackingRef.current.isTap = false;
        return;
      }
      if (target && target.closest && target.closest('[data-no-chart-tap]')) {
        tapTrackingRef.current.isTap = false;
        return;
      }
      // Read chartContainerRef.current at event time — the chart can mount
      // after this effect runs (loading state on first render), and we want
      // the tap detector to start working as soon as the chart is on screen.
      const container = chartContainerRef.current;
      if (!container) {
        tapTrackingRef.current.isTap = false;
        return;
      }
      const rect = container.getBoundingClientRect();
      if (touch.clientX < rect.left || touch.clientX > rect.right ||
          touch.clientY < rect.top || touch.clientY > rect.bottom) {
        tapTrackingRef.current.isTap = false;
        return;
      }
      tapTrackingRef.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        startTime: Date.now(),
        isTap: true,
      };
    };

    const handleTapMove = (e) => {
      const t = tapTrackingRef.current;
      if (!t.isTap) return;
      const touch = e.touches[0];
      if (!touch) return;
      const dx = touch.clientX - t.startX;
      const dy = touch.clientY - t.startY;
      if (Math.hypot(dx, dy) > 5) t.isTap = false;
    };

    const handleTapEnd = () => {
      const t = tapTrackingRef.current;
      if (t.isTap && Date.now() - t.startTime <= 300) {
        handleToggleCrosshairRef.current?.({
          touches: [{ clientX: t.startX, clientY: t.startY }],
        });
      }
      t.isTap = false;
    };

    const handleTapCancel = () => {
      tapTrackingRef.current.isTap = false;
    };

    window.addEventListener('touchstart', handleTapStart, { passive: true, capture: true });
    window.addEventListener('touchmove', handleTapMove, { passive: true, capture: true });
    window.addEventListener('touchend', handleTapEnd, { passive: true, capture: true });
    window.addEventListener('touchcancel', handleTapCancel, { passive: true, capture: true });

    return () => {
      window.removeEventListener('touchstart', handleTapStart, { capture: true });
      window.removeEventListener('touchmove', handleTapMove, { capture: true });
      window.removeEventListener('touchend', handleTapEnd, { capture: true });
      window.removeEventListener('touchcancel', handleTapCancel, { capture: true });
    };
  }, [isMobileView]);

  // The overlay legend (BB / SMA / EMA / PSAR) no longer lives here.
  //
  // Client, 5. Fix the mobile overlay position: "This should use the same
  // compact positioning logic as desktop, not a vertical position calculated
  // from the chart height."
  //
  // It used to be an absolutely positioned block at a hardcoded top offset
  // (74px desktop / 124px mobile) chosen to clear the price summary. On a phone
  // the summary is shorter than that, so the label floated in open chart space,
  // and any change to the rows above it broke the spacing again.
  //
  // It is now rendered as a normal child of the price summary itself — see
  // OverlayLegend in components/chart/chartTopSummary.js — so it is anchored to
  // the block above it on every breakpoint with no offset to maintain.


  const newDate = new Date();


const Legend1 = ({ items }) => (
  <div
    style={{
      position: "relative",
      top: isMobileView ? 0 : -28,
      marginBottom: isMobileView ? 0 : 0,
    }}
  >
    <div className={"flex flex-col gap-1"}>
      {/* OHLC display moved to ChartTopSummary for desktop */}
    </div>
  </div>
);


// const Legend1 = ({ items }) => (
//   <div style={{ position: "relative", top: -28}}>
//     <div className={"flex flex-col"}>
//       <p className={"font-samibold md:text-sm text-xs"}>
//         {symbol} (
//         <span className="font-samibold">
//           {moment(newDate).local().format("MMMM DD, YYYY hh:mm A")}
//         </span>
//         )
//       </p>
//     </div>
//   </div>
// );
  const showGrid = gridSetting.grid;

  useEffect(() => {
    setLines([]);
    setSetting({
      ...setting,
      clearOverlay: false,
      enableBollinger: false,
    });
    setLegendItems([]);
  }, [setting.clearOverlay]);

  const isEffectivelyEmpty = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) {
      return true;
    }
    // Check if all elements in the array are empty arrays
    return arr.every((item) => Array.isArray(item) && item.length === 0);
  };

  useEffect(() => {
    // Only clear indicators, NOT overlays - they should be independent
    setSetting({
      ...setting,
      clearIndicator: false,
    });
    setIndicatorConfig({
      indicatorConfig: [],
    });
    // REMOVED: setSetting overlayParams: [] - overlays should NOT be cleared when indicators are cleared
  }, [setting.clearIndicator, symbol]);

  const renderLineSeries = () => {
    return <>{lines}</>;
  };

  const getColorByLabelAndValue = (label, value, labelColorData) => {
    const matchedItem = labelColorData.find(
      (item) =>
        item.label.toLowerCase() === label.toLowerCase() && item.value === value
    );
    return matchedItem ? matchedItem.color : null;
  };

  useEffect(() => {
    let updatedLine = [];
    console.log("Overlay params:", setting.overlayParams);
    console.log("Chart data sample:", initialData?.[0]);
    console.log("Full data sample keys:", initialData?.[0] ? Object.keys(initialData[0]) : 'no data');
    if (initialData?.[0]?.chart) {
      console.log("Chart property:", initialData[0].chart);
    }

    if (setting.overlayParams && Array.isArray(setting.overlayParams)) {
      setting.overlayParams.forEach((item, index) => {
        if (
          item.inputValue === undefined ||
          item.inputValue === "" ||
          item.selectValue === ""
        ) {
          return;
        }
        if (item.selectValue === "sma") {
          updatedLine.push(
            <LineSeries
              key={`sma-${uuidv4()}`}
              yAccessor={(d) => {
                // Check multiple possible data structures
                if (d.chart && d.chart[index] && d.chart[index].sma) {
                  return d.chart[index].sma.sma || d.chart[index].sma;
                }
                if (d.sma) {
                  return d.sma;
                }
                // Check for direct property with period
                if (d[`sma_${item.inputValue}`]) {
                  return d[`sma_${item.inputValue}`];
                }
                console.log('SMA data not found for:', d);
                return null;
              }}
              strokeStyle={item.color}
              curve={curveMonotoneX}
            />
          );
        }

        if (item.selectValue === "ema") {
          updatedLine.push(
            <LineSeries
              key={`ema-${uuidv4()}`}
              yAccessor={(d) => {
                // Check multiple possible data structures
                if (d.chart && d.chart[index] && d.chart[index].ema) {
                  return d.chart[index].ema.ema || d.chart[index].ema;
                }
                if (d.ema) {
                  return d.ema;
                }
                // Check for direct property with period
                if (d[`ema_${item.inputValue}`]) {
                  return d[`ema_${item.inputValue}`];
                }
                console.log('EMA data not found for:', d);
                return null;
              }}
              strokeStyle={item.color}
              curve={curveMonotoneX}
            />
          );
          // updatedLine.push(
          //   <LineSeries
          //     key={`ema-top-${uuidv4()}`}
          //     yAccessor={(d) =>
          //       d.chart[index] && d.chart[index].ema && d.chart[index].ema.close
          //     }
          //     strokeStyle="#00FF00"
          //   />
          // );
        }

        if (item.selectValue === "psar") {
          updatedLine.push(
            <React.Fragment key={`psar-fragment-${uuidv4()}`}>
              <SARSeries
                key={`psar-top-${uuidv4()}`}
                yAccessor={(d) =>
                  d.chart && d.chart[index] && d.chart[index].psar &&
                  d.chart[index].psar.psarl
                }
                fillStyle={{ falling: item.color, raising: item.color }}
                highlightOnHover={true}
              />
              <SARSeries
                key={`psar-bottom-${uuidv4()}`}
                yAccessor={(d) =>
                  d.chart && d.chart[index] && d.chart[index].psar &&
                  d.chart[index].psar.psars
                }
                fillStyle={{ falling: item.color, raising: item.color }}
                highlightOnHover={true}
              />
            </React.Fragment>
          );
        }

        if (item.selectValue === "bb") {
          updatedLine.push(
            <LineSeries
              key={`bb-top-${uuidv4()}`}
              yAccessor={(d) =>
                d.chart[index] && d.chart[index].bb && d.chart[index].bb.top
              }
              strokeStyle="#2ca02c"
            />
          );
          updatedLine.push(
            <LineSeries
              key={`bb-bottom-${uuidv4()}`}
              yAccessor={(d) =>
                d.chart[index] && d.chart[index].bb && d.chart[index].bb.bottom
              }
              strokeStyle="#FF00FF"
            />
          );
          updatedLine.push(
            <LineSeries
              key={`bb-middle-${uuidv4()}`}
              yAccessor={(d) =>
                d.chart[index] && d.chart[index].bb && d.chart[index].bb.middle
              }
              //strokeStyle="#00FFF0"
              strokeStyle={item.color}
            />
          );
        }
      });
    }
    setLines(updatedLine);
  }, [setting.overlayParams]);

// const yTickValues = [...new Set(dataWithADL.map(d => +d.close?.toFixed(5)))];
// const needsRightMargin = yTickValues.some((value) => {
//   const valStr = value.toString();
//   const [_, decimal] = valStr.split(".");
//   return value < 1 && (decimal?.length || 0) >= 4;
// });

// Build unified tick formatter for margin estimation and axis rendering
// Asset class for this chart's price precision, from the symbol prefix
// (C:→forex, X:→crypto, else stock). Forex shows 4dp on EVERY price (incl. pairs
// >= 1 like EURUSD 1.1400); crypto shows 4dp under $1 (TRON 0.3300) and 2dp at/
// over $1 (BTC); stocks keep the original magnitude-based formatting.
const chartPriceClass = String(symbol || setting?.symbol || "").startsWith("C:")
  ? "forex"
  : String(symbol || setting?.symbol || "").startsWith("X:")
  ? "crypto"
  : "stock";
// Single source of truth for every price the y-axis / crosshair renders.
const fmtAxisPrice = (value) => {
  if (value == null) return "";
  const num = Number(value);
  if (!Number.isFinite(num)) return "";
  const absNum = Math.abs(num);
  // A whole value prints WITHOUT a trailing ".00"/".0000" (66000, not 66000.00),
  // but real fractional digits are kept (0.3300, 1.1400, 63419.35 stay as-is) —
  // the regex only strips a dot followed by all-zeros.
  if (chartPriceClass === "forex") return absNum.toFixed(4).replace(/\.0+$/, "");
  if (chartPriceClass === "crypto")
    return (absNum < 1 ? absNum.toFixed(4) : absNum.toFixed(2)).replace(/\.0+$/, "");
  // stock (original behaviour): integer for large, 2dp typical, more for sub-1.
  if (absNum >= 1000) return Math.round(absNum).toString();
  if (absNum >= 1) return absNum.toFixed(2).replace(/\.00$/, "").replace(/\.0$/, "");
  const decimals = (absNum.toString().split(".")[1] || "").length;
  const sub = absNum
    .toFixed(Math.max(4, Math.min(decimals || 4, 6)))
    .replace(/0+$/, "")
    .replace(/\.$/, "");
  const parts = sub.split(".");
  return parts.length === 2 && parts[1].length < 2 ? absNum.toFixed(2) : sub;
};

const formatYAxisTick = (value) => fmtAxisPrice(value);

// Stabilize margin calculation to prevent chart jumping on pan/zoom end
// Use ALL data close values to calculate max possible width needed, not just visible data
const allCloseValues = useMemo(() => {
  if (!dataWithADL || dataWithADL.length === 0) return [];
  return [...new Set(dataWithADL.map(d => +d.close?.toFixed(5)))];
}, [dataWithADL.length]); // Only recalculate when data length changes, not on every render

// Measure text width using canvas for accurate margin
const measureTextWidth = useCallback((text) => {
  try {
    const canvas = measureTextWidth._canvas || (measureTextWidth._canvas = document.createElement('canvas'));
    const ctx = canvas.getContext('2d');
    ctx.font = (isMobileView ? '12px' : '13px') + ' sans-serif';
    const m = ctx.measureText(String(text));
    return Math.ceil(m.width + 8);
  } catch {
    const approxCharWidth = isMobileView ? 7 : 8;
    return String(text).length * approxCharWidth + 8;
  }
}, [isMobileView]);

// Stabilize the margin so it doesn't change during pan/zoom interactions
const computedRightMargin = useMemo(() => {
  if (allCloseValues.length === 0) return 50; // Default margin
  const longestTickWidth = allCloseValues.reduce((max, v) => Math.max(max, measureTextWidth(formatYAxisTick(v))), 0) || 20;
  // Client: "the Y-axis looks like it's not using the full width anymore."
  // Commit 577d77e ("add Y-axis hover detection...") was a DESKTOP crosshair
  // change, but it also pushed the mobile buffer 10 -> 16. Desktop keeps the 20
  // it was given; mobile goes back to the 10 it had before that commit.
  const minimalRightBuffer = isMobileView ? 10 : 20;
  return Math.min(Math.max(Math.ceil(longestTickWidth + minimalRightBuffer), 26), 140);
}, [allCloseValues, measureTextWidth, isMobileView]);

// Dynamic margins: ensure topmost Y-axis label is not clipped;
// BTCUSD gets at least a small right buffer.
// When below indicators exist, the last below-indicator panel renders the
// date/time axis, so the main chart drops its bottom margin. Otherwise the
// main chart shows the bottom axis itself with margin 24.
//
// Client Rev 2, Trade 7: "Fix the chart X-axis. The date labels are currently
// clipped at the bottom and must be fully visible."
//
// The codebase already knew about this — see lastIndicatorMargin below, whose own
// comment reads "Mobile needs more headroom — the smaller viewport clips labels at
// 24px" and which therefore uses 36px on mobile. That fix was only ever applied to
// the indicator panel that draws the axis when below-indicators exist. The MAIN
// chart, which draws the axis itself whenever there are none, kept the flat 24px
// and so clipped its date labels on a phone — which is the state the client is
// looking at, since a league's Trade screen opens with no indicators.
// Same 36px, same reason, so the two cannot disagree again.
const margin = useMemo(() => ({
  left: 0,
  right: isBTCUSD ? Math.max(computedRightMargin, 32) : computedRightMargin,
  top: CHART_MARGIN_TOP,
  bottom: belowIndicatorCount > 0 ? 0 : (isMobileView ? 36 : 24)
}), [isBTCUSD, computedRightMargin, belowIndicatorCount, isMobileView]);

// Indicator panels must share the main chart's horizontal layout (left/right
// margin + padding) so the shared xScale maps each bar index to the same pixel
// x in every canvas. Without this, defaults differ from the main chart's
// dynamic right margin and bars on indicator panels appear shifted right.
const indicatorMargin = useMemo(() => ({
  left: margin.left,
  right: margin.right,
  top: 0,
  bottom: 10,
}), [margin]);

// Last below indicator needs extra bottom margin for the date/time axis labels.
// Mobile needs more headroom — the smaller viewport clips labels at 24px.
const lastIndicatorMargin = useMemo(() => ({
  left: margin.left,
  right: margin.right,
  top: 0,
  bottom: isMobileView ? 36 : 24,
}), [margin, isMobileView]);

// Client, 8: "The price chart and indicator panels must share the same plot
// width, bar spacing, and right padding."
//
// This was a flat { right: 5 } while the main canvas uses chartPadding, which is
// 18 on desktop and 5 on mobile. `padding.right` is dead space reserved inside
// the plot between the last bar and the price axis, so a 13px difference on
// desktop gave the indicator panels a 13px WIDER plot for the same bar range.
// The same xExtents then spread over more pixels, every bar sat fractionally
// further right than its candle, and the last one landed clear of the price
// chart's last candle — the "indicators extend one bar to the right" in the
// report, and why the crosshair did not intersect today's candle, today's RSI
// and today's ADL at one x.
//
// Sharing chartPadding is what makes the three panels agree by construction
// rather than by two numbers being kept equal by hand. Defined after
// chartPadding below via the same memo input.
const indicatorPadding = useMemo(
  () => ({ left: 0, right: isMobileView ? 5 : 18 }),
  [isMobileView]
);

// The main canvas's right padding is dead space between the last candle and the
// price axis. 577d77e raised it 5 -> 18 for BOTH breakpoints while fixing the
// desktop crosshair; on a 390px phone that 13px is visible, and together with
// the buffer above it is the width the client says the chart stopped using.
// Desktop keeps 18 — the client says desktop is fine. Mobile returns to 5,
// which is also what indicatorPadding above uses, so the main chart and any
// indicator panel share one horizontal layout again on mobile.
const chartPadding = useMemo(
  () => ({ left: 0, right: isMobileView ? 5 : 18 }),
  [isMobileView]
);

  const gridHeight = height - margin.top - margin.bottom;
  const totalHeight = gridHeight;
  const chartHeight = gridHeight / 2;
  const barChartHeight = gridHeight / 4;
  const gridWidth = width - margin.left - margin.right;

  // Indicator chart height - use the same fixed value we subtract from totalAvailable
  // so that main chart + indicators fit exactly into the container (no cutoff, no gap).
  const indicatorChartHeight = isMobileView
    ? (squareExport ? indicatorPanelHeight - 10 : 140)
    : indicatorPanelHeight;

  // Memoize barChartOrigin after barChartHeight is defined
  const barChartOrigin = useCallback((_, h) => [0, h - barChartHeight + 12], [barChartHeight]);

  // Tiny pixel buffer so data never touches the chart edge
  const candleChartPadding = useMemo(() => ({ top: 3, bottom: 2 }), []);

  // Memoize grid settings to prevent recreation on every render
  const yGrid = useMemo(() => showGrid
    ? {
        showGridLines: true,
        innerTickSize: -1 * gridWidth,
        tickStrokeDasharray: gridSetting.style,
        tickStrokeWidth: gridSetting.strokeWidth,
        gridLinesStrokeStyle: "#D3D3D3",
      }
    : {}, [showGrid, gridWidth, gridSetting.style, gridSetting.strokeWidth]);

  const xGrid = useMemo(() => showGrid
    ? {
        showGridLines: true,
        innerTickSize: -1 * gridHeight,
        tickStrokeDasharray: gridSetting.style,
        tickStrokeWidth: gridSetting.strokeWidth,
        gridLinesStrokeStyle: "#D3D3D3",
      }
    : {}, [showGrid, gridHeight, gridSetting.style, gridSetting.strokeWidth]);

  const [yAxisLabelX, yAxisLabelY] = [
    width - margin.left - 45,
    (height - margin.top - margin.bottom) / 2,
  ];

  const clampValue = (value, minValue, maxValue) =>
    Math.max(minValue, Math.min(maxValue, value));

  const getChartContentGroup = useCallback(() => {
    if (chartGroupRef.current && chartGroupRef.current.isConnected) {
      return chartGroupRef.current;
    }

    if (!chartContainerRef.current) return null;
    const svgElement = chartContainerRef.current.querySelector('svg');
    if (!svgElement) return null;
    const chartGroup = svgElement.querySelector('g[transform]');
    chartGroupRef.current = chartGroup;
    return chartGroup;
  }, []);

  const applyChartTransform = useCallback(
    (transformValue, originValue) => {
      const chartGroup = getChartContentGroup();
      if (!chartGroup) return;
      // Use GPU-accelerated transform for smooth 60fps
      chartGroup.style.transform = transformValue || '';
      if (originValue !== undefined) {
        chartGroup.style.transformOrigin = originValue;
      }
      // Hint browser to optimize for transform animations
      chartGroup.style.willChange = transformValue ? 'transform' : 'auto';
      // Ensure smooth rendering
      chartGroup.style.backfaceVisibility = 'hidden';
      chartGroup.style.perspective = '1000px';
    },
    [getChartContentGroup]
  );

  const resetChartTransform = useCallback(() => {
    const chartGroup = getChartContentGroup();
    if (!chartGroup) return;
    chartGroup.style.transform = '';
    chartGroup.style.transformOrigin = '';
    chartGroup.style.willChange = 'auto';
    chartGroup.style.backfaceVisibility = '';
    chartGroup.style.perspective = '';
  }, [getChartContentGroup]);

  // Calculate distance between two touch points
  const getTouchDistance = (touch1, touch2) => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Get the center point between two touches
  const getTouchCenter = (touch1, touch2) => {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2,
    };
  };

  // Handle touch events for mobile pinch-to-zoom
  // DISABLED - using +/- buttons instead
  const handleTouchStart = (e) => {
    // Pinch zoom disabled - do nothing
  };

  const handleTouchMove = (e) => {
    // Pinch zoom disabled - do nothing
  };

  const handleTouchEnd = (e) => {
    // Pinch zoom disabled - do nothing
  };

  // Mobile zoom button handlers
  const handleZoomIn = useCallback(() => {
    if (!chartCanvasComponentRef.current?.xAxisZoom) return;

    try {
      const xScale = chartCanvasComponentRef.current.state?.xScale;
      if (xScale) {
        const currentDomain = xScale.domain();

        // Save the position before first zoom operation
        if (!preZoomDomainRef.current) {
          preZoomDomainRef.current = [...currentDomain];
          console.log('[Chart] Saved pre-zoom position:', preZoomDomainRef.current);
        }

        const domainCenter = (currentDomain[0] + currentDomain[1]) / 2;
        const currentSpan = currentDomain[1] - currentDomain[0];

        // Zoom in by reducing span by 20%
        const newSpan = currentSpan * 0.8;
        const minSpan = 10;
        const clampedSpan = Math.max(minSpan, newSpan);

        const newDomainStart = domainCenter - clampedSpan / 2;
        const newDomainEnd = domainCenter + clampedSpan / 2;
        const newDomain = [newDomainStart, newDomainEnd];

        // Update cached extents BEFORE zoom to prevent recalculation during re-render
        lastXExtentsRef.current = newDomain;
        // Sync indicators that rely on xExtents prop (disableInteraction=true)
        setSyncedXExtents([...newDomain]);

        chartCanvasComponentRef.current.xAxisZoom(newDomain);
        console.log('[Chart] Zoom in applied:', { newSpan: clampedSpan, newDomain });
      }
    } catch (error) {
      console.debug('[Chart] Zoom in error:', error);
    }
  }, []);

  // Reset zoom to show the latest data (current trading day)
  const handleZoomReset = useCallback(() => {
    console.log('[Chart] handleZoomReset called, dataLength:', data.length);

    if (!chartCanvasComponentRef.current?.xAxisZoom) {
      console.log('[Chart] handleZoomReset: No xAxisZoom available');
      return;
    }

    try {
      const xScaleState = chartCanvasComponentRef.current.state?.xScale;
      if (!xScaleState) {
        console.log('[Chart] handleZoomReset: No xScale state');
        return;
      }

      // Use data.length (processed data) instead of initialData.length for consistency
      // This ensures we're working with the same data array that indicators reference
      const dataLength = data.length;

      if (dataLength === 0) {
        console.log('[Chart] handleZoomReset: dataLength is 0');
        return;
      }

      // Calculate a reasonable default view window (e.g., last 80-100 bars depending on screen size)
      const defaultBarsToShow = isMobileView ? 50 : 80;
      const barsToShow = Math.min(defaultBarsToShow, dataLength);

      // Set domain to show the most recent bars (current trading day area)
      const newDomainEnd = dataLength - 1;
      const newDomainStart = Math.max(0, newDomainEnd - barsToShow);

      const newDomain = [newDomainStart, newDomainEnd];

      console.log('[Chart] handleZoomReset: Applying new domain:', newDomain, 'bars:', barsToShow);

      // Apply zoom to main chart
      chartCanvasComponentRef.current.xAxisZoom(newDomain);
      console.log('[Chart] Zoom reset to current trading day:', { start: newDomainStart, end: newDomainEnd, barsShown: barsToShow, dataLength });

      // CRITICAL: Update lastXExtentsRef to ensure the new domain persists
      // This is needed because xExtents calculation uses this cached value
      lastXExtentsRef.current = newDomain;

      // NOTE: We no longer call xAxisZoom on indicators here.
      // The ChartCanvas handles zoom internally via xExtents prop.
      // Key-based remount only happens when data.length changes (historical data loads).
      console.log('[Chart] handleZoomReset: Indicator charts will update via xExtents prop');

      // Update synced extents immediately to ensure all charts stay in sync
      // Using flushSync to force synchronous state update
      console.log('[Chart] handleZoomReset: Setting syncedXExtents to:', newDomain);
      flushSync(() => {
        setSyncedXExtents([...newDomain]); // Create new array to force React to detect change
      });

      // Update cached domains to prevent sync loop from reverting the change
      lastMainDomainRef.current = [...newDomain];
      Object.keys(indicatorChartRefsRef.current).forEach((indicatorId) => {
        lastIndicatorDomainsRef.current[indicatorId] = [...newDomain];
      });

      // Clear the saved position so next zoom will save a new one
      preZoomDomainRef.current = null;

      console.log('[Chart] handleZoomReset: Complete');
    } catch (error) {
      console.debug('[Chart] Zoom reset error:', error);
    }
  }, [data.length, isMobileView]);

  const handleZoomOut = useCallback(() => {
    if (!chartCanvasComponentRef.current?.xAxisZoom) return;

    // Block zoom out while loading more data (one API call at a time)
    if (isLoadingMore) {
      console.log('[Chart] Zoom out blocked: already loading more data');
      return;
    }

    try {
      const xScale = chartCanvasComponentRef.current.state?.xScale;
      if (xScale) {
        const currentDomain = xScale.domain();

        // Save the position before first zoom operation
        if (!preZoomDomainRef.current) {
          preZoomDomainRef.current = [...currentDomain];
          console.log('[Chart] Saved pre-zoom position:', preZoomDomainRef.current);
        }

        const currentSpan = currentDomain[1] - currentDomain[0];
        const maxDataIndex = data.length > 0 ? data.length - 1 : 100;

        // Zoom out by increasing span by 25%
        const newSpan = currentSpan * 1.25;

        // Calculate new domain centered around current center
        let domainCenter = (currentDomain[0] + currentDomain[1]) / 2;
        let newDomainStart = domainCenter - newSpan / 2;
        let newDomainEnd = domainCenter + newSpan / 2;

        // If we're at or near the left edge (domain start <= 0),
        // shift the viewport to the right to allow continued zoom out
        if (newDomainStart <= 0) {
          // Anchor to left edge (0) and expand only to the right
          newDomainStart = 0;
          newDomainEnd = newSpan;

          console.log('[Chart] Zoom out: anchored to left edge, expanding right', {
            newDomainStart,
            newDomainEnd,
            maxDataIndex
          });

          // If we need more data than available, trigger API call
          if (newDomainEnd > maxDataIndex || currentDomain[0] <= 0) {
            // Clamp to available data for now
            newDomainEnd = Math.min(newDomainEnd, maxDataIndex);

            // Only trigger API if we're truly at the left edge and have more data potentially
            if (currentDomain[0] <= 0 && onLoadMore && !isLoadingMore) {
              console.log('[Chart] Triggering onLoadMore from zoom out button (left edge reached)');

              // Preserve current viewport
              if (chartCanvasComponentRef.current?.state?.xScale) {
                preservedDomainRef.current = [...currentDomain];
              }

              // DO NOT set loading state here - let BuyChart control it
              // This prevents race conditions where we set loading=true but the fetch is debounced
              onLoadMore({
                startIndex: 0,
                endIndex: Math.floor(newDomainEnd),
                reason: 'zoomOutButton'
              });
            }
          }
        } else {
          // Normal case: not at left edge, clamp both sides
          newDomainStart = Math.max(0, newDomainStart);
          newDomainEnd = Math.min(maxDataIndex, newDomainEnd);
        }

        // Ensure we always have a valid span (at least some zoom effect)
        const finalSpan = newDomainEnd - newDomainStart;
        if (finalSpan < currentSpan) {
          // Can't zoom out anymore in this direction, just scroll right
          newDomainStart = Math.max(0, maxDataIndex - currentSpan);
          newDomainEnd = maxDataIndex;
          console.log('[Chart] Zoom out: scrolling to show rightmost bars', {
            newDomainStart,
            newDomainEnd
          });
        }

        // Apply the zoom
        const newDomain = [newDomainStart, newDomainEnd];

        // Update cached extents BEFORE zoom to prevent recalculation during re-render
        lastXExtentsRef.current = newDomain;
        // Sync indicators that rely on xExtents prop (disableInteraction=true)
        setSyncedXExtents([...newDomain]);

        chartCanvasComponentRef.current.xAxisZoom(newDomain);
        console.log('[Chart] Zoom out applied:', {
          newDomainStart,
          newDomainEnd,
          span: newDomainEnd - newDomainStart,
          dataLength: data.length
        });
      }
    } catch (error) {
      console.debug('[Chart] Zoom out error:', error);
    }
  }, [data.length, isLoadingMore, onLoadMore, setIsLoadingMore]);

// Ensure xExtents is valid - memoize to prevent recalculation on every render
// IMPORTANT: When interval (period/symbol) changes, always show most recent data
// Also use syncedXExtents when set by handleZoomReset for indicator sync
const safeXExtents = useMemo(() => {
  // After interval change, always position to show the most recent bars
  // This fixes the issue where switching from monthly to weekly/daily shows old dates
  if (justChangedIntervalRef.current && data.length > 0) {
    const visibleBars = isMobileView ? 50 : 100;
    const dataMax = data.length - 1;
    const newExtents = [Math.max(0, dataMax - visibleBars), dataMax];
    return newExtents;
  }

  // Use xExtents first - this includes the properly calculated viewport
  // (handles historical data prepend with shifted domain)
  if (Array.isArray(xExtents) && xExtents.length === 2 &&
      Number.isFinite(xExtents[0]) && Number.isFinite(xExtents[1]) &&
      xExtents[1] > xExtents[0]) {
    return xExtents;
  }

  // Calculate safe fallback based on current data
  if (data.length > 0) {
    const visibleBars = isMobileView ? 50 : 100;
    const dataMax = data.length - 1;
    const fallback = [Math.max(0, dataMax - visibleBars), dataMax];
    return fallback;
  }
  return undefined;
}, [xExtents, data.length, isMobileView]);

// Clear the interval change flag after rendering with new data
// This allows subsequent user panning/zooming to work normally
useEffect(() => {
  if (justChangedIntervalRef.current && data.length > 0) {
    // Schedule the flag clear for the next frame to ensure the chart has rendered
    const timeoutId = setTimeout(() => {
      justChangedIntervalRef.current = false;
      console.log('[Chart] Interval change flag cleared - normal panning/zooming re-enabled');
    }, 100); // Small delay to ensure chart has fully initialized with new data
    return () => clearTimeout(timeoutId);
  }
}, [data.length, intervalKey]);


// Memoize safeYExtents helper to prevent recreation on every render
const safeYExtents = useCallback((fn, fallback = [0, 1]) => {
  return (d) => {
    try {
      const v = fn?.(d);
      if (Array.isArray(v)) return v;
      if (Number.isFinite(v)) return v;
      return fallback;
    } catch {
      return fallback;
    }
  };
}, []);



  const mainChart = (
    <div
      draggable={false}
      ref={chartContainerRef}
      onMouseDown={(e) => {
        e.preventDefault();
        onChartMouseDown(e);
        // On mobile, the crosshair toggle is now driven by a tap detector in
        // onChartTouchEnd — not by mousedown — so a scroll/pan touchstart no
        // longer activates the crosshair. Desktop ignored this anyway since
        // handleToggleCrosshair early-returns when !isMobileView.
      }}
      onMouseMove={onChartMouseMove}
      onMouseUp={onChartMouseUp}
      onMouseLeave={onChartMouseLeave}
      onTouchStart={onChartTouchStart}
      onTouchMove={onChartTouchMove}
      onTouchEnd={onChartTouchEnd}
      onContextMenu={(e) => e.preventDefault()}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onPaste={(e) => e.preventDefault()}
      onSelectStart={(e) => e.preventDefault()}
      onSelect={(e) => e.preventDefault()}
      style={{
        position: "relative",
        // "none" so a touch on the chart pans only left/right (handled by our
        // JS handlers, which use deltaX only). "pan-y" let the browser scroll
        // the page vertically during a chart drag — the up/down "shake" with no
        // purpose. Matches the indicator chart containers, which already use "none".
        touchAction: "none",
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
        WebkitTapHighlightColor: "transparent",
      }}
      className={`chart-item chart-main`}
    >
      {/* Mobile zoom controls kept inside chart just above x-axis labels */}
      {isMobileView && !hideZoomControls && (
        <div
          style={{
            position: "absolute",
            bottom: `${margin.bottom + 6}px`,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: (setting?.drawMode && !endpointsOverControls) ? 30 : 20,
            display: "flex",
            flexDirection: "row",
            gap: "10px",
            padding: "2px 8px",
            pointerEvents: "none",
          }}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleZoomOut();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className="chart-zoom-btn"
            style={{
              width: "34px",
              height: "34px",
              borderRadius: "10px",
              border: "1px solid rgba(0,0,0,0.15)",
              color: setting?.colorMode === "light" ? "#333" : "#fff",
              fontSize: "18px",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "auto",
            }}
            aria-label="Zoom Out"
          >
            −
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleZoomIn();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className="chart-zoom-btn"
            style={{
              width: "34px",
              height: "34px",
              borderRadius: "10px",
              border: "1px solid rgba(0,0,0,0.15)",
              color: setting?.colorMode === "light" ? "#333" : "#fff",
              fontSize: "18px",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "auto",
            }}
            aria-label="Zoom In"
          >
            +
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleZoomReset();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className="chart-zoom-btn"
            style={{
              width: "34px",
              height: "34px",
              borderRadius: "10px",
              border: "1px solid rgba(0,0,0,0.15)",
              color: setting?.colorMode === "light" ? "#333" : "#fff",
              fontSize: "16px",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "auto",
            }}
            aria-label="Reset Zoom"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
              <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
              <path d="M8 16H3v5" />
            </svg>
          </button>
        </div>
      )}
      {isChartsRoute && (
        <>
          <Legend1 items={legendItems} />
        </>
      )}
        <ChartCanvas
        key={intervalKey}
        ref={chartCanvasComponentRef}
        height={height}
         margin={margin}
              ratio={3}
        width={width}
           padding={chartPadding}
        data={dataWithADL}
        displayXAccessor={displayXAccessor}
          seriesName="Data"
        xScale={xScale}
        xAccessor={xAccessor}
        xExtents={safeXExtents}
        disableZoom={false}
        disablePan={false}
        disableInteraction={false}
        zoomAnchor={lastVisibleItemBasedZoomAnchor}
        mouseMoveEvent={true}
        panEvent={!isLoadingMore}
        zoomEvent={true}
        clamp={false}
        maintainPointsPerPixelOnResize={true}
        onLoadBefore={onLoadMore ? (startIndex, endIndex) => {
          // Skip if already loading to prevent multiple triggers
          if (isLoadingMore) {
            console.log('[ChartComp] onLoadBefore skipped: already loading');
            return;
          }

          console.log('[ChartComp] onLoadBefore triggered:', { startIndex, endIndex, dataLength: data.length });

          // Preserve current viewport position before loading historical data
          if (chartCanvasComponentRef.current?.state?.xScale) {
            const currentDomain = chartCanvasComponentRef.current.state.xScale.domain();
            if (currentDomain && currentDomain.length === 2) {
              preservedDomainRef.current = [...currentDomain];
              console.log('[ChartComp] Preserved viewport domain:', preservedDomainRef.current);
            }
          }

          // DO NOT set loading state here - let BuyChart control it
          // This prevents race conditions where we set loading=true but the fetch is debounced
          if (onLoadMore) {
            onLoadMore({ startIndex, endIndex, reason: 'onLoadBefore' });
          }
        } : undefined}
      >
        {setting?.title && (
          <Label
            x={(width - margin.left - margin.right) / 2}
            y={30}
            fontSize="30"
            fillStyle="#153A5B"
            text={setting?.titleText}
          />
        )}

        <Chart
          id={2}
          height={barChartHeight}
          origin={barChartOrigin}
           yExtents={safeYExtents(barChartExtents)}
        >
          {setting.chartOverlay === "on" ? (
            <BarSeries fillStyle={volumeColor} yAccessor={volumeSeries} />
          ) : null}
        </Chart>
        <Chart id={3} height={totalHeight} yExtentsCalculator={candleChartExtentsCalculator} padding={candleChartPadding}>
          {/* <XAxis
            strokeStyle={"#8d8d8d"}
            tickStrokeStyle={"#8d8d8d"}
                  <Annotate
                    with={LabelAnnotation}
                    when={(d, i) => {
                      const condition = i % 4 === 0 && d.close > max - 5;
                      const condition1 = i % 4 === 0;
                      console.log("Condition check:", condition, d.close);
                      return !condition ? condition1 : condition;
                    }}
                    usingProps={{
                      text: (d) => `${d.close.toFixed(2)}`,
                      fill: "#000000", // Annotation text color
                      textAnchor: "middle",
                      fontFamily: "Arial Black, Impact, Helvetica, Arial, sans-serif",
                      fontSize: 16,
                      tooltip: "Parabolic SAR",
                      x: ({ xScale, xAccessor, datum }) => xScale(xAccessor(datum)), // Position the annotation based on the X value
                      y: ({ yScale, datum }) => yScale(datum.close), // Position the annotation based on the PSAR value
                    }}
                  />
            tickStrokeStyle={"#8d8d8d"}
            showTicks={false}
            xZoomHeight={1}
            tickPadding={0}
            axisAt={"top"}
            orient={"top"}
          />
          <YAxis
            strokeStyle={"#8d8d8d"}
            tickStrokeStyle={"#8d8d8d"}
            showTicks={false}
            yZoomWidth={1}
            tickPadding={0}
            axisAt={"left"}
            orient={"left"}
          /> */}
          <XAxis
  axisAt="top"
  orient="top"
  showTicks={false}
  showDomain={false}
  stroke="transparent"
/>

{/* Show bottom XAxis date labels on main chart only when no below indicators.
   When below indicators exist, the last one shows the date labels instead. */}
{belowIndicatorCount === 0 && (
  <XAxis
    axisAt="bottom"
    orient="bottom"
    strokeStyle="#e5e7eb"
    tickStrokeStyle="#e5e7eb"
  />
)}

{/* <YAxis
  axisAt="left"
  orient="left"
  showTicks={false}
  showDomain={false}
  stroke="transparent"
/> */}

<YAxis
  axisAt="right"
  orient="right"
  showTicks={true}
  showDomain={false}
  strokeStyle="#8d8d8d"
  tickStrokeStyle="#8d8d8d"
  ticks={6}
  tickFormat={fmtAxisPrice}
/>

          {/* <CustomGrid xScale={xScale} yScale={yScale} chartHeight={height} chartWidth={width} /> */}
          {/*<LineSeries*/}
          {/*  key={12}*/}
          {/*  data={psarData}*/}
          {/*  yAccessor={(d) => d.PSARs}*/}
          {/*  strokeStyle="#00ff00"*/}
          {/*/>*/}
          {setting.enablePrice ? (
            <Annotate
              with={LabelAnnotation}
              when={(d, i) => {
                return i % 10 === 0; // Show price label every 10 bars
              }}
              usingProps={{
                text: (d) =>
                  d && typeof d.close === "number" ? d.close.toFixed(2) : "",
                fill: "#000000",
                textAnchor: "middle",
                fontFamily: "Helvetica",
                fontSize: 11,
                fontWeight: "bold",
                tooltip: "Price Label",
                x: ({ xScale, xAccessor, datum }) => xScale(xAccessor(datum)),
                y: ({ yScale, datum }) => yScale(datum.close) - 10,
              }}
            />
          ) : null}



{/* {setting.enablePrice && (() => {
  const visibleData = dataWithADL.slice();
  if (!visibleData || visibleData.length === 0) return null;

  const formatPrice = (price) => {
    const diff = Math.abs(price);
    return diff >= 1 ? price.toFixed(2) : price.toFixed(4);
  };

  // Find absolute highest high and lowest low
  const absHigh = visibleData.reduce((max, d) => (d.high > max.high ? d : max), visibleData[0]);
  const absLow = visibleData.reduce((min, d) => (d.low < min.low ? d : min), visibleData[0]);

  // Detect swings
  const points = [];
  let lastType = null;
  const minGap = (Math.max(...visibleData.map(d => d.high)) -
                  Math.min(...visibleData.map(d => d.low))) * 0.1; // 10% gap filter

  for (let i = 1; i < visibleData.length - 1; i++) {
    const prev = visibleData[i - 1];
    const curr = visibleData[i];
    const next = visibleData[i + 1];

    if (curr.high > prev.high && curr.high > next.high) {
      if (lastType !== "max" && (points.length === 0 || Math.abs(curr.high - points[points.length - 1].price) > minGap)) {
        points.push({ type: "max", price: curr.high, index: i });
        lastType = "max";
      }
    }
    if (curr.low < prev.low && curr.low < next.low) {
      if (lastType !== "min" && (points.length === 0 || Math.abs(curr.low - points[points.length - 1].price) > minGap)) {
        points.push({ type: "min", price: curr.low, index: i });
        lastType = "min";
      }
    }
  }

  // Sort by price importance
  points.sort((a, b) => Math.abs(b.price) - Math.abs(a.price));
  let topPoints = points.slice(0, 6);

  // Ensure absolute high and low are included
  if (!topPoints.some(p => p.price === absHigh.high)) {
    topPoints.push({ type: "max", price: absHigh.high, index: visibleData.indexOf(absHigh) });
  }
  if (!topPoints.some(p => p.price === absLow.low)) {
    topPoints.push({ type: "min", price: absLow.low, index: visibleData.indexOf(absLow) });
  }

  // Sort back by index (time)
  topPoints.sort((a, b) => a.index - b.index);

  console.log("Final swing points:", topPoints);

  return (
    <>
      {topPoints.map((p, idx) => {
        const datum = visibleData[p.index];
        return (
          <Annotate
            key={`point-${idx}`}
            with={LabelAnnotation}
            when={(bar, i) => i === p.index}
            usingProps={{
              text: () => formatPrice(p.price),
              fill: p.type === "max" ? "#2ca02c" : "#ff0000",
              fontSize: 12,
              fontFamily: "Helvetica",
              textAnchor: "middle",
              tooltip: p.type === "max" ? "Swing High" : "Swing Low",
              x: ({ xScale, xAccessor }) => xScale(xAccessor(datum)),
              y: ({ yScale }) =>
                p.type === "max"
                  ? yScale(p.price) - 12
                  : yScale(p.price) + 12,
            }}
          />
        );
      })}
    </>
  );
})()} */}



{setting.enablePrice && (() => {
  const visibleData = dataWithADL.slice();
  if (!visibleData || visibleData.length === 0) return null;

  const formatPrice = (price) => {
    const diff = Math.abs(price);
    return diff >= 1 ? price.toFixed(2) : price.toFixed(4);
  };

  const absHigh = visibleData.reduce((max, d) => (d.high > max.high ? d : max), visibleData[0]);
  const absLow = visibleData.reduce((min, d) => (d.low < min.low ? d : min), visibleData[0]);

  // Pure percentage-based labeling: only show label if price moves up/down by threshold from last label
  const points = [];
  const percentThreshold = 0.10; // 5% move required (change as needed)
  let lastLabelIndex = 0;
  let lastLabelPrice = visibleData[0]?.close;

  // Always include first bar
  // if (visibleData.length > 0) {
  //   points.push({ price: lastLabelPrice, index: 0, type: "init" });
  // }

const swingLookback = 8; // number of bars to check back
const swingLookahead = 8; // number of bars to check ahead
const minDiff = 0;

for (let i = swingLookback; i < visibleData.length - swingLookahead; i++) {
  const curr = visibleData[i];
  console.log("Current bar:", curr);


  const pastPrices = visibleData.slice(i - swingLookback, i).map(d => d.close);
  console.log("Past prices:", pastPrices);

  const futurePrices = visibleData.slice(i + 1, i + swingLookahead + 1).map(d => d.close);

  // Swing High: current close is greater than all past closes AND greater than all next closes
  if (pastPrices.every(p => curr.close > p) && futurePrices.every(p => curr.close > p)) {
    if (Math.abs(curr.close - lastLabelPrice) >= minDiff) {   // 👈 check difference
      points.push({ price: curr.close, index: i, type: "swingHigh" });
      lastLabelPrice = curr.close; // update last label price
    }
  }

  // Swing Low: current close is lower than all past closes AND lower than all next closes
  if (pastPrices.every(p => curr.close < p) && futurePrices.every(p => curr.close < p)) {
    if (Math.abs(curr.close - lastLabelPrice) >= minDiff) {   // 👈 check difference
      points.push({ price: curr.close, index: i, type: "swingLow" });
      lastLabelPrice = curr.close; // update last label price
    }
  }
}

  // Sort by time
  points.sort((a, b) => a.index - b.index);

  // return (
  //   <>
  //     {points.map((p, idx) => {
  //       const datum = visibleData[p.index];
  //       return (
  //         <Annotate
  //           key={`point-${idx}`}
  //           with={LabelAnnotation}
  //           when={(bar, i) => i === p.index}
  //           usingProps={{
  //             text: () => `────▶ ${formatPrice(p.price)}`,
  //             fill: "#000000",
  //             fontSize: 12,
  //             fontWeight: "bold",
  //             fontFamily: "Arial Black, Impact, Arial, sans-serif",
  //             textAnchor: "start",
  //             tooltip:
  //               p.type === "high"
  //                 ? "Absolute High"
  //                 : p.type === "low"
  //                 ? "Absolute Low"
  //                 : p.type === "up"
  //                 ? "Up Move"
  //                 : p.type === "down"
  //                 ? "Down Move"
  //                 : "Price Label",
  //             x: ({ xScale, xAccessor }) => xScale(xAccessor(datum)),
  //             y: ({ yScale }) => yScale(p.price) + 4,
  //           }}
  //         />
  //       );
  //     })}
  //   </>
  // );


  // ...inside your setting.enablePrice && (() => { ... }) block...

// ...inside your setting.enablePrice && (() => { ... }) block...

// return (
//   <>
//     {points.map((p, idx) => {
//       const datum = visibleData[p.index];
//       return (
//         <Annotate
//           key={`point-${idx}`}
//           with={LabelAnnotation}
//           when={(bar, i) => i === p.index}
//           usingProps={{
//             text: () => `────▶ ${formatPrice(p.price)}`,
//             fill: "#000000",
//             fontSize: 12,
//             fontWeight: "bold",
//             fontFamily: "Arial Black, Impact, Arial, sans-serif",
//             textAnchor: "start",
//             tooltip:
//               p.type === "high"
//                 ? "Absolute High"
//                 : p.type === "low"
//                 ? "Absolute Low"
//                 : p.type === "up"
//                 ? "Up Move"
//                 : p.type === "down"
//                 ? "Down Move"
//                 : "Price Label",
//             x: ({ xScale, xAccessor }) => {
//               const x = xScale(xAccessor(datum));
//               const minX = 10;
//               const maxX = width - 80;
//               return Math.max(minX, Math.min(x, maxX));
//             },
//             y: ({ yScale }) => {
//               const y = yScale(p.price) + 4;
//               const minY = 18;
//               const maxY = height - 28; // Increased margin from bottom
//               return Math.max(minY, Math.min(y, maxY));
//             },
//           }}
//         />
//       );
//     })}
//   </>
// );


// ...inside your setting.enablePrice && (() => { ... }) block...

return (
  <>
    {/* {points.map((p, idx) => {
      const datum = visibleData[p.index];
      return (
        <Annotate
          key={`point-${idx}`}
          with={LabelAnnotation}
          when={(bar, i) => i === p.index}
          usingProps={{
            text: () => `────▶ ${formatPrice(p.price)}`,
            fill: "#000000",
            fontSize: 12,
            fontWeight: "bold",
            fontFamily: "Arial Black, Impact, Arial, sans-serif",
            textAnchor: "start",
tooltip:
  p.type === "swingHigh"
    ? "Swing High"
    : p.type === "swingLow"
    ? "Swing Low"
    : "Price Label",

x: ({ xScale, xAccessor }) => {
  const x = xScale(xAccessor(datum));
  const minX = 10;
  const rightMargin = isMobileView ? 160 : 100; // Increase for mobile/desktop
  const maxX = width - rightMargin;
  return Math.max(minX, Math.min(x, maxX));
},
            y: ({ yScale }) => {
              const y = yScale(p.price) + 4;
              const minY = 18;
              const maxY = height - 28;
              return Math.max(minY, Math.min(y, maxY));
            },
          }}
        />
      );
    })} */}

    {/* new */}
{points.map((p, idx) => {
  const d = visibleData[p.index];
  const isHigh = p.type === "swingHigh";

  const wickGap = isMobileView ? 2 : 3;
  const fs  = isMobileView ? 11 : 12;
  const pad = isMobileView ? 4  : 5;
  const tW  = isMobileView ? 10 : 12;
  const tH  = isMobileView ? 6  : 7;

  return (
    <Annotate
      key={`callout-${idx}`}
      with={CalloutAnnotation}
      when={(bar, i) => i === p.index}
      usingProps={{
        text: () => `${formatPrice(p.price)}`,
        direction: isHigh ? "down" : "up",
        x: ({ xScale, xAccessor }) => xScale(xAccessor(d)),
        y: ({ yScale }) =>
          isHigh ? yScale(d.high) - wickGap : yScale(d.low) + wickGap,

        // styling
        fontSize: fs,
        padding: pad,
        tailWidth: tW,
        tailHeight: tH,
        bg: "#e7eff3",
        stroke: "#a6b8c1",
        fill: "#000000",
        radius: 3,
        opacity: 1,

        // 👇 new props for edge handling
        isFirst: idx === 0,
        isLast: idx === points.length - 1,
        width: 1435, // pass your chart/container width here
      }}
    />
  );
})}








  </>
);

// ...inside your setting.enablePrice && (() => { ... }) block...

// return (
//   <>
//     {points.map((p, idx) => {
//       const datum = visibleData[p.index];
//       const x = xScale(xAccessor(datum));
//       const minX = 40;
//       const rightMargin = isMobileView ? 160 : 100;
//       const maxX = width - rightMargin;
//       const labelX = Math.max(minX, Math.min(x, maxX));
//       const nearLeft = labelX <= minX + 5;
//       const nearRight = labelX >= maxX - 5;

//       return (
//         <Annotate
//           key={`point-${idx}`}
//           with={LabelAnnotation}
//           when={(bar, i) => i === p.index}
//           usingProps={{
//             text: () => (nearLeft || nearRight ? formatPrice(p.price) : `────▶ ${formatPrice(p.price)}`),
//             fill: "#000000",
//             fontSize: 12,
//             fontWeight: "bold",
//             fontFamily: "Arial Black, Impact, Arial, sans-serif",
//             textAnchor: "start",
//             tooltip:
//               p.type === "high"
//                 ? "Absolute High"
//                 : p.type === "low"
//                 ? "Absolute Low"
//                 : p.type === "up"
//                 ? "Up Move"
//                 : p.type === "down"
//                 ? "Down Move"
//                 : "Price Label",
//             x: () => labelX,
//             y: ({ yScale }) => {
//               let labelY;
//               if (nearLeft || nearRight) {
//                 labelY =
//                   p.type === "high"
//                     ? yScale(p.price) - 16
//                     : yScale(p.price) + 16;
//               } else {
//                 labelY = yScale(p.price) + 4;
//               }
//               const minY = 18;
//               const maxY = height - 28;
//               return Math.max(minY, Math.min(labelY, maxY));
//             },
//           }}
//         />
//       );
//     })}
//   </>
// );
})()}









          {setting.chartType === "candlestick" ? <CandlestickSeries /> : null}
          {setting.chartType === "solid" ? (
            <LineSeries
              yAccessor={(d) => d.close}
              strokeStyle="blue"
              strokeDasharray="none"
              strokeWidth={2}
            />
          ) : null}
          {renderLineSeries()}

          {/* <MouseCoordinateY
            at="left"
            orient="left"
            rectWidth={margin.right}
            displayFormat={pricesDisplayFormat}
          /> */}

          <MovingAverageTooltip origin={[8, 24]} options={[]} />

          {setting.quote ? <OHLCTooltip origin={[8, 16]} /> : null}
          {setting.chartType === "ohlc" ? <OHLCSeries /> : null}

          {/* X-Axis Crosshair Coordinate - Shows date/time when hovering */}
          {/* Only on main chart when no below indicators; otherwise last indicator shows it */}
          {(!isMobileView || showMobileCrosshair) && belowIndicatorCount === 0 && (
            <MouseCoordinateX
              at="bottom"
              orient="bottom"
              displayFormat={timeFormat("%Y-%m-%d %H:%M")}
              rectWidth={120}
              fill="#4a4a4a"
              textFill="#ffffff"
            />
          )}

          {/* Y-Axis price badge is now rendered as a custom CSS div (hoverPrice state)
              outside ChartCanvas so it works over the empty area after the last candle.
          {(!isMobileView || showMobileCrosshair) && (
            <MouseCoordinateY
              at="right"
              orient="right"
              displayFormat={fmtAxisPrice}
              fill="#4a4a4a"
              textFill="#ffffff"
            />
          )}
          */}

          {/* Left Y-Axis Crosshair Coordinate (optional mirror) */}
          {(!isMobileView || showMobileCrosshair) && (
            <MouseCoordinateY
              at="left"
              orient="left"
              displayFormat={fmtAxisPrice}
              fill="#4a4a4a"
              textFill="#ffffff"
            />
          )}

          {setting?.xLabel && (
            <Label
              x={(width - margin.left - margin.right) / 2}
              y={height + 29}
              fontSize="25"
              fillStyle="#153A5B"
              text={setting?.xLabelText}
            />
          )}

          {setting?.yLabel && (
            <Label
              x={yAxisLabelX}
              y={yAxisLabelY}
              fillStyle="#153A5B"
              rotate={-90}
              fontSize="25"
              text={setting?.yLabelText}
            />
          )}
        </Chart>

        {/* The visible crosshair lines are CSS overlays (indicatorCrosshairRef +
            horizontalCrosshairRef) on the outer wrapper so a single continuous
            vertical line spans the main chart and any below-indicator panels.
            We still mount a transparent CrossHairCursor so the canvas's mouse-
            tracking pipeline keeps firing — that's what drives MouseCoordinateY's
            price tag refresh on the right Y-axis. */}
        <CrossHairCursor
          strokeStyle="transparent"
          strokeWidth={0}
          strokeDasharray="Solid"
        />
      </ChartCanvas>
    </div>
  );

  const generateRandomColor = () => {
    return (
      "#" +
      Math.floor(Math.random() * 16777215)
        .toString(16)
        .padStart(6, "0")
    );
  };

  const assignUniqueColors = (data) => {
    const labelSeen = {};

    return data.map((item) => {
      const label = item.label;
      if (label in labelSeen) {
        return { ...item, color: generateRandomColor() };
      } else {
        labelSeen[label] = item.color;
        return item;
      }
    });
  };

  /* useEffect(() => {
    const activeOverlays = setting.overlayParams
      .map((overlay) => {
        if (overlay.selectValue !== "" && overlay.inputValue !== "") {
          switch (overlay.selectValue) {
            case "bb":
              return {
                label: "BB",
                color: "#00FFF0",
                value: overlay.inputValue,
              };
            case "ema":
              return {
                label: "EMA",
                color: "#FF00FF",
                value: overlay.inputValue,
              };
            case "psar":
              return {
                label: "PSAR",
                color: "black",
                value: overlay.inputValue,
              };
            case "sma":
              return {
                label: "SMA",
                color: "#2ca02c",
                value: overlay.inputValue,
              };
            default:
              return null;
          }
        }
      })
      .filter(Boolean); // Filter out any null entries

    // SD
    const updatedData = assignUniqueColors(activeOverlays);

    setUpdatedLegendItems(updatedData);
    setLegendItems(updatedData);
  }, [setting.overlayParams]); */

  // SD
  useEffect(() => {
    const activeOverlays = setting.overlayParams
      .map((overlay) => {
        if (overlay.selectValue !== "" && overlay.inputValue !== "") {
          switch (overlay.selectValue) {
            case "bb":
              return {
                label: "BB",
                color: overlay.color,
                value: overlay.inputValue,
              };
            case "ema":
              return {
                label: "EMA",
                color: overlay.color,
                value: overlay.inputValue,
              };
            case "psar":
              return {
                label: "PSAR",
                color: overlay.color,
                value: overlay.inputValue,
              };
            case "sma":
              return {
                label: "SMA",
                color: overlay.color,
                value: overlay.inputValue,
              };
            default:
              return null;
          }
        }
      })
      .filter(Boolean); // Filter out any null entries

    setLegendItems(activeOverlays);
  }, [setting.overlayParams]);

  // Helper to get safe xExtents for indicator - MUST sync with main chart
  // For indicators using main chart data (like ADL), use syncedXExtents directly
  const getIndicatorExtents = useCallback((indicatorData, useMainChartData = false) => {
    // Get the indicator's data length
    // If useMainChartData is true (for fallback indicators that use main chart's data),
    // use main chart data length directly
    const indicatorDataLength = useMainChartData ? (data?.length || 0) : (indicatorData?.length || 0);
    // Also get the main chart's data length for reference
    const mainDataLength = data?.length || 0;

    // If no data available, return a safe default
    if (indicatorDataLength === 0 || mainDataLength === 0) {
      // Return last 50 bars of main data as fallback
      const defaultBars = isMobileView ? 50 : 80;
      return [Math.max(0, mainDataLength - defaultBars), Math.max(0, mainDataLength - 1)];
    }

    // CRITICAL: For indicators using main chart data, ALWAYS use syncedXExtents directly
    // This ensures perfect synchronization with the main chart
    if (useMainChartData) {
      // Use syncedXExtents first (set by handleZoomReset), then fall back to calculated xExtents
      const currentExtents = syncedXExtents || xExtents;

      if (currentExtents && currentExtents.length === 2) {
        // Clamp to valid range
        const clampedStart = Math.max(0, Math.min(Math.round(currentExtents[0]), mainDataLength - 1));
        const clampedEnd = Math.max(clampedStart, Math.min(Math.round(currentExtents[1]), mainDataLength - 1));
        return [clampedStart, clampedEnd];
      }

      // Default: show last N bars
      const defaultBars = isMobileView ? 50 : 80;
      return [Math.max(0, mainDataLength - defaultBars), mainDataLength - 1];
    }

    // For specialized indicators with their own data
    const currentExtents = syncedXExtents || xExtents;
    if (!currentExtents || currentExtents.length < 2) {
      // Fallback: show last 50 bars of indicator data
      return [Math.max(0, indicatorDataLength - 50), indicatorDataLength - 1];
    }

    // If indicator data length matches main chart, use extents directly
    if (indicatorDataLength === mainDataLength) {
      const clampedStart = Math.max(0, Math.min(Math.round(currentExtents[0]), indicatorDataLength - 1));
      const clampedEnd = Math.max(clampedStart, Math.min(Math.round(currentExtents[1]), indicatorDataLength - 1));
      return [clampedStart, clampedEnd];
    }

    // For indicators with different data lengths, proportionally map the extents
    let [start, end] = currentExtents;

    // Calculate the ratio between indicator data and main chart data
    const ratio = indicatorDataLength / mainDataLength;

    // Map extents proportionally
    start = Math.floor(start * ratio);
    end = Math.floor(end * ratio);

    // Clamp to valid range for indicator data
    const clampedStart = Math.max(0, Math.min(start, indicatorDataLength - 1));
    const clampedEnd = Math.max(clampedStart, Math.min(end, indicatorDataLength - 1));

    // Ensure we show at least some data (minimum 10 bars if available)
    const minBarsToShow = Math.min(10, indicatorDataLength);
    if (clampedEnd - clampedStart < minBarsToShow) {
      return [Math.max(0, clampedEnd - minBarsToShow), clampedEnd];
    }

    return [clampedStart, clampedEnd];
  }, [syncedXExtents, xExtents, data?.length, isMobileView]);


  const renderIndicators = () => {
    const aboveChart = [];
    const belowChart = [];
    const indicatorCharts = [];

    if (isEffectivelyEmpty(indicatorConfig)) {
      return mainChart;
    }

    // Count below indicators to determine which is the last one
    const belowIndicators = indicatorConfig.filter(ind => ind.position === "below");
    const totalBelowCount = belowIndicators.length;
    let currentBelowIndex = 0;

    indicatorConfig.forEach((indicator, index) => {
      // Check if this indicator uses a specialized component with its own data processing
      // or falls back to the generic ChartCanvas which uses main chart's data (only ADL now)
      const hasSpecializedComponent = SPECIALIZED_INDICATOR_IDS.has(indicator.id);

      // Track indicator data length for proportional sync in the animation-frame loop
      if (hasSpecializedComponent && indicator?.data?.length) {
        indicatorDataLengthsRef.current[indicator.id] = indicator.data.length;
      }

      // Get extents for this indicator
      // For fallback indicators (like ADL, ADX), use the EXACT same extents as main chart (safeXExtents)
      // This guarantees perfect synchronization
      const indicatorExtents = hasSpecializedComponent
        ? getIndicatorExtents(indicator?.data, false)  // Use indicator's own data
        : safeXExtents;                                 // Use EXACT same extents as main chart

      // Determine if this is the last below indicator (for showing X-axis only on last one)
      const isLastBelowIndicator = indicator.position === "below" &&
        currentBelowIndex === totalBelowCount - 1;

      // Track current below indicator index
      if (indicator.position === "below") {
        currentBelowIndex++;
      }

      // Key should only change when data length changes (historical data loads)
      // NOT when zoom/pan changes extents - ChartCanvas handles that internally
      // This prevents double-line flicker during zoom operations
      const stableKey = `${indicator.id}-${data.length}`;

      const chartItem = (
        <div
          key={`chart-canvas-container-${index}-${stableKey}`}
          style={{ position: "relative", marginBottom: 0, touchAction: "none" }}
          className={`chart-item indicator-chart-item`}
          onMouseDown={onIndicatorMouseDown}
          onMouseMove={onIndicatorMouseMove}
          onMouseUp={onIndicatorMouseUp}
          onMouseLeave={onIndicatorMouseLeave}
          onTouchStart={onIndicatorTouchStart}
          onTouchMove={onIndicatorTouchMove}
          onTouchEnd={onIndicatorTouchEnd}
        >
          {/* Panel indicator label — client, Indicator Polish 1: "For indicators
              displayed in their own panel, such as ADL and RSI, remove the
              colored square. Use the same smaller, muted gray label style as the
              updated BB and SMA overlays. Keep the parameter only when
              meaningful, such as `RSI (14)`. Display only `ADL` if its `0`
              parameter has no purpose."

              The square is gone rather than turned into a line the way the
              overlay swatches were: an overlay swatch identifies WHICH of
              several lines sharing the price panel is which, and a panel
              indicator is alone in its own panel, so the colour carries no
              information here.

              Same type as the overlay legend — 14px / weight 500 / #6B7280 (the
              `secondary` token) — so the two label systems read as one. */}
          {/* Client, 6. Move panel labels inside their panels: "On both screens,
              labels such as RSI, ADL, PPO, and OBV sit directly against the top
              border. Give them approximately 8 pixels of top space and 12 pixels
              of left space so they feel intentionally placed rather than
              attached to the border."

              Was top:0 / left:18 — flush against the panel's top edge, and out
              of line with the 12px left inset the price summary and overlay
              legend use.

              Client, 4. Standardize all chart labels: Poppins 14px weight 500 in
              the muted grey, stated explicitly (family included) so this label
              and the overlay legend cannot pick up different stacks. */}
          <div style={{ position: "absolute", top: 8, left: 12, zIndex: 5 }}>
            <div
              style={{
                fontFamily: "Poppins, sans-serif",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: 1.2,
                color: "#6B7280",
              }}
            >
              {indicatorPanelLabel(indicator)}
            </div>
          </div>
          <div style={{ position: "relative" }} className={`abcd`}>
            {indicator.id === "MACDH" ? (
              <MACDHIndicator
                ref={(el) => {
                  if (el) {
                    indicatorChartRefsRef.current[indicator.id] = el;
                  } else {
                    delete indicatorChartRefsRef.current[indicator.id];
                    delete lastIndicatorDomainsRef.current[indicator.id];
                    delete indicatorDataLengthsRef.current[indicator.id];
                  }
                }}
                key={`macdh-${stableKey}`}
                height={indicatorChartHeight}
                ratio={3}
                width={width}
                initialData={dataWithADL}
                displayXAccessor={displayXAccessor}
                xScale={xScale}
                xAccessor={xAccessor}
                xExtents={safeXExtents}
                margin={isLastBelowIndicator ? lastIndicatorMargin : indicatorMargin}
                padding={indicatorPadding}
                showXAxis={isLastBelowIndicator}
                fast={indicator.fast || 12}
                slow={indicator.slow || 26}
                signal={indicator.signal || 9}
              />
            ) : indicator.id === "MACD" ? (
              <MACDIndicator
                ref={(el) => {
                  if (el) {
                    indicatorChartRefsRef.current[indicator.id] = el;
                  } else {
                    delete indicatorChartRefsRef.current[indicator.id];
                    delete lastIndicatorDomainsRef.current[indicator.id];
                    delete indicatorDataLengthsRef.current[indicator.id];
                  }
                }}
                key={`macd-${stableKey}`}
                height={indicatorChartHeight}
                ratio={3}
                width={width}
                initialData={dataWithADL}
                displayXAccessor={displayXAccessor}
                xScale={xScale}
                xAccessor={xAccessor}
                xExtents={safeXExtents}
                margin={isLastBelowIndicator ? lastIndicatorMargin : indicatorMargin}
                padding={indicatorPadding}
                showXAxis={isLastBelowIndicator}
                fast={indicator.fast || 12}
                slow={indicator.slow || 26}
                signal={indicator.signal || 9}
              />
            ) : indicator.id === "PPO" ? (
              <PPOIndicator
                ref={(el) => {
                  if (el) {
                    indicatorChartRefsRef.current[indicator.id] = el;
                  } else {
                    delete indicatorChartRefsRef.current[indicator.id];
                    delete lastIndicatorDomainsRef.current[indicator.id];
                    delete indicatorDataLengthsRef.current[indicator.id];
                  }
                }}
                key={`ppo-${stableKey}`}
                height={indicatorChartHeight}
                ratio={3}
                width={width}
                initialData={dataWithADL}
                displayXAccessor={displayXAccessor}
                xScale={xScale}
                xAccessor={xAccessor}
                xExtents={safeXExtents}
                margin={isLastBelowIndicator ? lastIndicatorMargin : indicatorMargin}
                padding={indicatorPadding}
                showXAxis={isLastBelowIndicator}
                fast={indicator.fast || 12}
                slow={indicator.slow || 26}
                signal={indicator.signal || 9}
              />
            ) : indicator.id === "PVO" ? (
              <PVOIndicator
                ref={(el) => {
                  if (el) {
                    indicatorChartRefsRef.current[indicator.id] = el;
                  } else {
                    delete indicatorChartRefsRef.current[indicator.id];
                    delete lastIndicatorDomainsRef.current[indicator.id];
                    delete indicatorDataLengthsRef.current[indicator.id];
                  }
                }}
                key={`pvo-${stableKey}`}
                height={indicatorChartHeight}
                ratio={3}
                width={width}
                initialData={dataWithADL}
                displayXAccessor={displayXAccessor}
                xScale={xScale}
                xAccessor={xAccessor}
                xExtents={safeXExtents}
                margin={isLastBelowIndicator ? lastIndicatorMargin : indicatorMargin}
                padding={indicatorPadding}
                showXAxis={isLastBelowIndicator}
                fast={indicator.fast || 12}
                slow={indicator.slow || 26}
                signal={indicator.signal || 9}
              />
            ) : indicator.id === "CCI" ? (
              <CCIIndicator
                ref={(el) => {
                  if (el) {
                    indicatorChartRefsRef.current[indicator.id] = el;
                  } else {
                    delete indicatorChartRefsRef.current[indicator.id];
                    delete lastIndicatorDomainsRef.current[indicator.id];
                    delete indicatorDataLengthsRef.current[indicator.id];
                  }
                }}
                key={`cci-${stableKey}`}
                height={indicatorChartHeight}
                ratio={3}
                width={width}
                initialData={dataWithADL}
                displayXAccessor={displayXAccessor}
                xScale={xScale}
                xAccessor={xAccessor}
                xExtents={safeXExtents}
                margin={isLastBelowIndicator ? lastIndicatorMargin : indicatorMargin}
                padding={indicatorPadding}
                showXAxis={isLastBelowIndicator}
                windowSize={indicator.windowSize || 20}
              />
            ) : indicator.id === "CMF" ? (
              <CMFIndicator
                ref={(el) => {
                  if (el) {
                    indicatorChartRefsRef.current[indicator.id] = el;
                  } else {
                    delete indicatorChartRefsRef.current[indicator.id];
                    delete lastIndicatorDomainsRef.current[indicator.id];
                    delete indicatorDataLengthsRef.current[indicator.id];
                  }
                }}
                key={`cmf-${stableKey}`}
                height={indicatorChartHeight}
                ratio={3}
                width={width}
                initialData={dataWithADL}
                displayXAccessor={displayXAccessor}
                xScale={xScale}
                xAccessor={xAccessor}
                xExtents={safeXExtents}
                margin={isLastBelowIndicator ? lastIndicatorMargin : indicatorMargin}
                padding={indicatorPadding}
                showXAxis={isLastBelowIndicator}
                windowSize={indicator.windowSize || 20}
              />
            ) : indicator.id === "RSI" ? (
              <RSIIndicator
                ref={(el) => {
                  if (el) {
                    indicatorChartRefsRef.current[indicator.id] = el;
                  } else {
                    delete indicatorChartRefsRef.current[indicator.id];
                    delete lastIndicatorDomainsRef.current[indicator.id];
                    delete indicatorDataLengthsRef.current[indicator.id];
                  }
                }}
                key={`rsi-${stableKey}`}
                height={indicatorChartHeight}
                ratio={3}
                width={width}
                initialData={dataWithADL}
                displayXAccessor={displayXAccessor}
                xScale={xScale}
                xAccessor={xAccessor}
                xExtents={safeXExtents}
                margin={isLastBelowIndicator ? lastIndicatorMargin : indicatorMargin}
                padding={indicatorPadding}
                showXAxis={isLastBelowIndicator}
                windowSize={indicator.windowSize || 14}
              />
            ) : indicator.id === "MFI" ? (
              <MFIIndicator
                ref={(el) => {
                  if (el) {
                    indicatorChartRefsRef.current[indicator.id] = el;
                  } else {
                    delete indicatorChartRefsRef.current[indicator.id];
                    delete lastIndicatorDomainsRef.current[indicator.id];
                    delete indicatorDataLengthsRef.current[indicator.id];
                  }
                }}
                key={`mfi-${stableKey}`}
                height={indicatorChartHeight}
                ratio={3}
                width={width}
                initialData={dataWithADL}
                displayXAccessor={displayXAccessor}
                xScale={xScale}
                xAccessor={xAccessor}
                xExtents={safeXExtents}
                margin={isLastBelowIndicator ? lastIndicatorMargin : indicatorMargin}
                padding={indicatorPadding}
                showXAxis={isLastBelowIndicator}
                windowSize={indicator.windowSize || 14}
              />
            ) : indicator.id === "EFI" ? (
              <EFIndicator
                ref={(el) => {
                  if (el) {
                    indicatorChartRefsRef.current[indicator.id] = el;
                  } else {
                    delete indicatorChartRefsRef.current[indicator.id];
                    delete lastIndicatorDomainsRef.current[indicator.id];
                    delete indicatorDataLengthsRef.current[indicator.id];
                  }
                }}
                key={`efi-${stableKey}`}
                height={indicatorChartHeight}
                ratio={3}
                width={width}
                initialData={dataWithADL}
                displayXAccessor={displayXAccessor}
                xScale={xScale}
                xAccessor={xAccessor}
                xExtents={safeXExtents}
                margin={isLastBelowIndicator ? lastIndicatorMargin : indicatorMargin}
                padding={indicatorPadding}
                showXAxis={isLastBelowIndicator}
                windowSize={indicator.windowSize || 13}
              />
            ) : indicator.id === "ADX" ? (
              <ADXIndicator
                ref={(el) => {
                  if (el) {
                    indicatorChartRefsRef.current[indicator.id] = el;
                  } else {
                    delete indicatorChartRefsRef.current[indicator.id];
                    delete lastIndicatorDomainsRef.current[indicator.id];
                    delete indicatorDataLengthsRef.current[indicator.id];
                  }
                }}
                key={`adx-${stableKey}`}
                height={indicatorChartHeight}
                ratio={3}
                width={width}
                initialData={dataWithADL}
                displayXAccessor={displayXAccessor}
                xScale={xScale}
                xAccessor={xAccessor}
                xExtents={safeXExtents}
                margin={isLastBelowIndicator ? lastIndicatorMargin : indicatorMargin}
                padding={indicatorPadding}
                showXAxis={isLastBelowIndicator}
                windowSize={indicator.windowSize || 14}
              />
            ) : indicator.id === "OBV" ? (
              <OBVIndicator
                ref={(el) => {
                  if (el) {
                    indicatorChartRefsRef.current[indicator.id] = el;
                  } else {
                    delete indicatorChartRefsRef.current[indicator.id];
                    delete lastIndicatorDomainsRef.current[indicator.id];
                    delete indicatorDataLengthsRef.current[indicator.id];
                  }
                }}
                key={`obv-${stableKey}`}
                height={indicatorChartHeight}
                ratio={3}
                width={width}
                initialData={dataWithADL}
                displayXAccessor={displayXAccessor}
                xScale={xScale}
                xAccessor={xAccessor}
                xExtents={safeXExtents}
                margin={isLastBelowIndicator ? lastIndicatorMargin : indicatorMargin}
                padding={indicatorPadding}
                showXAxis={isLastBelowIndicator}
              />
            ) : indicator.id === "ROC" ? (
              <ROCIndicator
                ref={(el) => {
                  if (el) {
                    indicatorChartRefsRef.current[indicator.id] = el;
                  } else {
                    delete indicatorChartRefsRef.current[indicator.id];
                    delete lastIndicatorDomainsRef.current[indicator.id];
                    delete indicatorDataLengthsRef.current[indicator.id];
                  }
                }}
                key={`roc-${stableKey}`}
                height={indicatorChartHeight}
                ratio={3}
                width={width}
                initialData={dataWithADL}
                displayXAccessor={displayXAccessor}
                xScale={xScale}
                xAccessor={xAccessor}
                xExtents={safeXExtents}
                margin={isLastBelowIndicator ? lastIndicatorMargin : indicatorMargin}
                padding={indicatorPadding}
                showXAxis={isLastBelowIndicator}
                windowSize={indicator.windowSize || 12}
              />
            ) : indicator.id === "STOCH" ? (
              <STOCHIndicator
                ref={(el) => {
                  if (el) {
                    indicatorChartRefsRef.current[indicator.id] = el;
                  } else {
                    delete indicatorChartRefsRef.current[indicator.id];
                    delete lastIndicatorDomainsRef.current[indicator.id];
                    delete indicatorDataLengthsRef.current[indicator.id];
                  }
                }}
                key={`stoch-${stableKey}`}
                height={indicatorChartHeight}
                ratio={3}
                width={width}
                initialData={dataWithADL}
                displayXAccessor={displayXAccessor}
                xScale={xScale}
                xAccessor={xAccessor}
                xExtents={safeXExtents}
                margin={isLastBelowIndicator ? lastIndicatorMargin : indicatorMargin}
                padding={indicatorPadding}
                showXAxis={isLastBelowIndicator}
                kPeriod={indicator.kPeriod || 14}
                kSlowing={indicator.kSlowing || 3}
                dPeriod={indicator.dPeriod || 3}
              />
            ) : (
              <ChartCanvas
                key={`indicator-canvas-${indicator.id}-${stableKey}`}
                ref={(el) => {
                  if (el) {
                    indicatorChartRefsRef.current[indicator.id] = el;
                  } else {
                    delete indicatorChartRefsRef.current[indicator.id];
                    delete lastIndicatorDomainsRef.current[indicator.id];
                    delete indicatorDataLengthsRef.current[indicator.id];
                  }
                }}
                height={indicatorChartHeight}
                ratio={3}
                width={width}
                data={dataWithADL}
                displayXAccessor={displayXAccessor}
                seriesName={"Data" + index}
                xScale={xScale}
                xAccessor={xAccessor}
                xExtents={indicatorExtents}
                margin={isLastBelowIndicator ? lastIndicatorMargin : indicatorMargin}
                padding={indicatorPadding}
                disableZoom={false}
                disablePan={false}
                disableInteraction={false}
                clamp={false}
                zoomAnchor={lastVisibleItemBasedZoomAnchor}
              >
                {/* padding: same fix as the specialised indicator components —
                    8px of vertical breathing room at each end so the extremes
                    (ADL at its current level, RSI below 30) are not clipped
                    against the panel edge. It shrinks the y RANGE only; the y
                    domain, the shared xScale and xExtents are untouched, so the
                    panel stays locked to price through pan and zoom. */}
                <Chart
                  key={`indicator-chart-${index}-${stableKey}`}
                  id={indicator.id}
                  yExtents={safeYExtents(indicator.id === "ADL" ? (d) => d?.adl : indicator.yExtents)}
                  origin={(w, h) => [0, 0]}
                  padding={{ top: 8, bottom: 8 }}
                >
                  {isLastBelowIndicator ? (
                    <XAxis
                      axisAt="bottom"
                      orient="bottom"
                      strokeStyle="#8d8d8d"
                      tickStrokeStyle="#8d8d8d"
                      tickLabelFill="#4a4a4a"
                    />
                  ) : (
                    <XAxis
                      strokeStyle="#e5e7eb"
                      tickStrokeStyle="#e5e7eb"
                      showTicks={false}
                      showTickLabel={false}
                      xZoomHeight={1}
                      tickPadding={0}
                    />
                  )}
                  <YAxis
                    strokeStyle="#e5e7eb"
                    tickStrokeStyle="#e5e7eb"
                    ticks={5}
                    tickFormat={(value) => {
                      if (value == null) return "";
                      const num = Number(value);
                      if (Number.isFinite(num)) {
                        const absNum = Math.abs(num);
                        // Show decimals only if not a whole number
                        return absNum % 1 === 0 ? absNum.toString() : absNum.toFixed(1);
                      }
                      // Fallback: remove any negative signs from string
                      return String(value).replace(/^\s*-\s*/, "").replace(/^\s*−\s*/, "");
                    }}
                  />
                  <XAxis
                    strokeStyle="#e5e7eb"
                    tickStrokeStyle="#e5e7eb"
                    showTicks={false}
                    xZoomHeight={1}
                    tickPadding={0}
                    axisAt={"top"}
                    orient={"top"}
                  />
                  <YAxis
                    strokeStyle="#e5e7eb"
                    tickStrokeStyle="#e5e7eb"
                    showTicks={false}
                    yZoomWidth={1}
                    tickPadding={0}
                    axisAt={"left"}
                    orient={"left"}
                  />
                  {indicator.lineConfig.map((item, lineIndex) => {
                    // For ADL, use d.adl since dataWithADL has this structure
                    // Other indicators use item.yAccessor from their config
                    const effectiveYAccessor = indicator.id === "ADL" ? (d) => d?.adl : item.yAccessor;

                    return (
                      <React.Fragment key={`${indicator.id}-line-${lineIndex}`}>
                        {item.lineType ? (
                          <LineSeries
                            id={indicator.id + lineIndex}
                            yAccessor={effectiveYAccessor}
                            strokeStyle={item.strokeStyle}
                          />
                        ) : (
                          <BarSeries
                            yAccessor={effectiveYAccessor}
                            fill={(d) => (d.macdh?.macdh > 0 ? "green" : "red")}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}

                  {/* Y-axis crosshair value tag for the indicator panel */}
                  {(!isMobileView || showMobileCrosshair) && (
                    <MouseCoordinateY
                      at="right"
                      orient="right"
                      displayFormat={(value) => {
                        if (value == null) return "";
                        const num = Number(value);
                        if (!Number.isFinite(num)) return String(value);
                        return nFormatter(num, 2);
                      }}
                      fill="#4a4a4a"
                      textFill="#ffffff"
                    />
                  )}
                </Chart>

                {/* Invisible CrossHairCursor keeps the canvas's mouse-tracking
                    pipeline running so MouseCoordinateY refreshes on hover.
                    The visible crosshair lines are the CSS overlays. */}
                <CrossHairCursor
                  strokeStyle="transparent"
                  strokeWidth={0}
                  strokeDasharray="Solid"
                />
              </ChartCanvas>
            )}
          </div>
        </div>
      );

      if (indicator.position === "above" && indicator.selectValue !== "") {
        aboveChart.push(chartItem);

        aboveChartCounterRef.current += 1; // Update the ref value
      }
      if (indicator.position === "below") {
        belowChart.push(chartItem);
      }
    });
    indicatorCharts.push(aboveChart);
    indicatorCharts.push(mainChart);
    // Wrap below indicators in a container with ref for real-time pan sync
    indicatorCharts.push(
      <div
        key="below-indicators-container"
        ref={belowIndicatorContainerRef}
        style={{ overflow: 'hidden', position: 'relative' }}
      >
        {belowChart}
      </div>
    );

    if (aboveChart.length == 0) {
      aboveChartCounterRef.current = 0;
    }

    return indicatorCharts;
  };

  useEffect(() => {
    if (aboveChartCounterRef.current >= 1) {
      const position = getChartMainItemPosition();

      const newTop = position.top - chartMainItemInitialPosition.top;

      setCanvasStyle((prevStyle) => ({
        ...prevStyle,
        top: newTop,
      }));
    } else {
      setCanvasStyle((prevStyle) => ({
        ...prevStyle,
        top: 0,
      }));
    }
    const event = new CustomEvent("indicatorChangeEvent", {
      detail: true,
    });
    document.dispatchEvent(event);
  }, [indicatorConfig]); // Depend on indicatorConfig

  // Early return after all hooks if no initial data was provided
  if (!hasValidInitialData || data.length === 0) {
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        backgroundColor: setting?.colorMode === 'light' ? 'white' : '#1a1a1a',
        color: setting?.colorMode === 'light' ? '#333' : '#ccc'
      }}>
        <div style={{ fontSize: '16px', marginBottom: '10px' }}>Loading chart data...</div>
        <div style={{ fontSize: '12px', opacity: 0.7 }}>{symbol || setting?.symbol || 'Loading...'}</div>
      </div>
    );
  }

  return (
    <>
      {isOverYAxis && <style>{`* { cursor: none !important; }`}</style>}
      <div
        onMouseMove={isMobileView ? undefined : updateChartHoverFromEvent}
        onMouseLeave={isMobileView ? undefined : clearChartHover}
        style={
          setting.colorMode === "light"
            ? {
                backgroundColor: "white",
                position: "relative",
                margin: isMobileView ? "3px 0px 0px" : "0px",
                // border: "1px solid #D3D3D3", // light gray border
              }
            : { backgroundColor: "#00000096", position: "relative", margin: isMobileView ? "3px 0px 0px" : "0px" }
        }
      >
        {renderIndicators()}

        {/* Single CSS-overlay crosshair for the entire chart stack.
            Vertical line spans the full outer wrapper (top:0 to bottom:0) so
            there is no break at the main-chart/indicator-panel boundary and
            no doubling against a canvas-drawn crosshair. The horizontal line
            is scoped to the main chart bounds via updateChartHoverFromEvent. */}
        <div
          ref={indicatorCrosshairRef}
          data-html2canvas-ignore="true"
          style={{
            display: 'none',
            position: 'absolute',
            top: 0,
            bottom: 0,
            width: '1px',
            background: 'repeating-linear-gradient(to bottom, #333333 0px, #333333 6px, transparent 6px, transparent 8px)',
            pointerEvents: 'none',
            zIndex: 10,
          }}
        />
        <div
          ref={horizontalCrosshairRef}
          data-html2canvas-ignore="true"
          style={{
            display: 'none',
            position: 'absolute',
            left: 0,
            right: 0,
            height: '1px',
            background: 'repeating-linear-gradient(to right, #333333 0px, #333333 6px, transparent 6px, transparent 8px)',
            pointerEvents: 'none',
            zIndex: 10,
          }}
        />

        {/* Custom CSS price badge on right Y-axis — independent of ChartCanvas internal events.
            Driven by hoverPrice state which is calculated from yScale directly in
            updateChartHoverFromEvent, so it persists even over empty chart area.

            Client: "The value is cut off though as you move up. Move it up and you
            will see the value gets cut off." — the leading digit disappears, so
            82.69 reads as 2.69.

            The cause is the box, not the number. This badge used to be a FIXED
            width — Math.max(margin.right - 2, 48) — with the price centred inside
            it and `whiteSpace: nowrap`. Two things make that fragile:

              • The width is derived from margin.right, and margin.right is sized
                from the AXIS TICK strings ("83", "82.80"). The badge does not show
                a tick: it shows the exact price under the finger, which carries
                more digits than the tick it sits between. The box is therefore
                measured against the wrong string.
              • The text is WHITE. When a nowrap line is wider than its flex
                container, it overflows the container's edges — and the black
                background only paints the box, so whatever hangs outside is white
                text on the white chart. It is not clipped, it is invisible, which
                looks exactly like the value being cut off.

            So the box sizes to its own content: `padding` guarantees breathing
            room, and with no fixed `width` the box simply grows leftwards
            whenever the number needs more room (it is anchored `right`). A digit
            can no longer end up outside it, at any price, on any symbol, in any
            font.

            `minWidth` is the resting footprint, and it is the gutter MINUS
            BADGE_AXIS_INSET — see that constant for why. It used to be the whole
            gutter, which is what put the badge on top of the newest candle
            (client 0014839). A price longer than the gutter still pushes past
            that resting width; never clipping the number wins over the inset,
            because an unreadable price is the worse bug of the two.

            `top` is clamped to the chart as well, so at the very top and very
            bottom of the scale the badge stays fully on the chart instead of half
            of it sitting outside. */}
        {hoverPrice && (!isMobileView || showMobileCrosshair) && (
          <div
            data-html2canvas-ignore="true"
            style={{
              position: 'absolute',
              right: 1,
              top: Math.max(0, Math.min(Math.round(hoverPrice.yPx) - 10, Math.max(0, (height || 0) - 20))),
              minWidth: Math.max(margin.right - BADGE_AXIS_INSET, 0),
              height: 20,
              padding: '0 6px',
              boxSizing: 'border-box',
              // Client: "the League Play Trade screen crosshairs black price
              // label issue remains." It is this badge, and it was the only
              // BLACK thing in the crosshair — every other coordinate label the
              // chart draws (the X-axis date, and the Y-axis labels on the
              // indicator panels) is #4a4a4a. Now QUSHi's dark teal, so the
              // price under the cursor reads as part of the product rather than
              // as a browser default.
              //
              // One badge serves both screens: `/charts` and `/:slug/trade`
              // render the same <Char> -> Chart.js, so there is no League Play
              // copy of this to fix separately.
              background: '#004C50',
              color: '#ffffff',
              fontSize: 11,
              fontFamily: 'sans-serif',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              zIndex: 20,
              whiteSpace: 'nowrap',
              borderRadius: 3,
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }}
          >
            {fmtAxisPrice(hoverPrice.price)}
          </div>
        )}

        {/* TradingView-style date/time overlay anchored to the bottom of the
            entire chart stack. Only shown when sub-panel indicators are below
            (otherwise the main chart's MouseCoordinateX already shows it). */}
        {belowIndicatorCount > 0 && chartHoverInfo && (
          <div
            style={{
              position: "absolute",
              bottom: 24,
              left: Math.round(chartHoverInfo.x) - 60,
              width: 120,
              textAlign: "center",
              background: "#4a4a4a",
              color: "#ffffff",
              padding: "2px 6px",
              borderRadius: 2,
              fontSize: 11,
              lineHeight: "14px",
              pointerEvents: "none",
              zIndex: 50,
              whiteSpace: "nowrap",
            }}
          >
            {chartHoverInfo.dateStr}
          </div>
        )}

        {/* Wrapper div for AnnotationStage to enable CSS transform during pan */}
        <div
          ref={annotationStageRef}
          style={{
            position: 'absolute',
            top: canvasStyle.top || 0,
            left: canvasStyle.left || 0,
            zIndex: canvasStyle.zIndex || 7,
            pointerEvents: canvasStyle.pointerEvents || 'none',
            transformOrigin: '0 0',
          }}
        >
          <AnnotationStage
            key={setting.symbol}
            style={{ ...canvasStyle, position: 'relative', top: 0, left: 0 }}
            setting={setting}
            width={width}
            height={height}
            data={data}
            xScale={chartScalesRef.current.xScale}
            yScale={chartScalesRef.current.yScale}
            xAccessor={xAccessor}
            scaleUpdateCounter={scaleUpdateCounter}
            chartScalesRef={chartScalesRef}
            hideZoomControls={hideZoomControls}
            onEndpointsOverControlsChange={handleEndpointsOverControlsChange}
            isMobileView={isMobileView}
            controlMarginBottom={margin.bottom}
          />
        </div>
      </div>
    </>
  );
};

export default ChartComp;
